/**
 * Client Configuration Service
 * 
 * Provides access to client-specific configuration loaded from Firestore.
 * Config is fetched directly from Firestore at runtime.
 * This service is the single source of truth for all client-specific settings.
 * 
 * The config is set in window.__CLIENT_CONFIG by the bootstrap process in index.tsx.
 */

import { HeroLayout } from '../types';
import type { ProducerSignatureConfig } from '../types';
import type { RsvpComponentConfig } from '../types/rsvp';
import {
  formatCastingLineupMusicMaxMb,
  resolveCastingLineupMusicMaxBytes,
} from '../utils/castingShowLineupMusicLimits';
import type {
  EventCoordinatorComponentConfig,
  EventCoordinatorInquiryType,
  EventInquiryFormComponentConfig,
} from '../types/eventCoordinator';
import { SubscriberListComponentConfig } from './shopService';
import { getEnvironment } from '../utils/environmentDetection';
import type { ImageCardMediaType, ImageFocusPoint } from '../utils/imageCardMedia';
import { FONT_PRESETS, getFontPreset } from '../config/fonts';
import type { ContractDocumentStyle } from '../utils/contractDocumentStyle';
import type {
  SectionMediaCornerRadiusPreset,
  IwtBlocksGapPreset,
  IwtBlockPaddingPreset,
} from './sectionVisualPresets';
import type { MembersAreaHubOverlay } from '../constants/membersAreaRegistry';
import { pathMatchesAnyNavHidePattern } from '../utils/navHidePathPatterns';
import type { Capability } from '../constants/capabilities';

export type { SectionMediaCornerRadiusPreset, IwtBlocksGapPreset, IwtBlockPaddingPreset };

declare global {
  interface Window {
    __CLIENT_CONFIG?: ClientConfig;
  }
}

/** Semantic text roles for Layout → Typography (matches section Text variants). */
export const THEME_TYPOGRAPHY_ROLES = ['headline', 'subheadline', 'body', 'caption', 'label'] as const;
export type ThemeTypographyRole = (typeof THEME_TYPOGRAPHY_ROLES)[number];

/** Preset id applied when `theme.typography` omits a role or has an unknown id. */
export const DEFAULT_THEME_TYPOGRAPHY_PRESET_ID = 'inter';

/** Scales fluid headline clamps (section text + hero). */
export type ThemeHeadlineScale = 'default' | 'large' | 'xl';

/** Per text variant: size, rhythm, and tracking (saved under `theme.typography.tuning.variants`). */
export type ThemeTypographyVariantTuning = {
  /** Multiplies fluid `clamp()` font size for this variant. */
  sizeScale: number;
  lineHeight: number;
  /** Letter-spacing in em (can be negative for headlines). */
  letterSpacingEm: number;
  /** Block margin before this variant’s `.section-text` row. */
  marginTopRem: number;
  /** Block margin after this variant’s `.section-text` row. */
  marginBottomRem: number;
};

/** Default section typography: Inter-forward, light display type, airy body, wide uppercase labels (model portfolio preview). */
export const DEFAULT_THEME_TYPOGRAPHY_VARIANTS: Record<ThemeTypographyRole, ThemeTypographyVariantTuning> = {
  headline: {
    sizeScale: 1,
    lineHeight: 1.12,
    letterSpacingEm: -0.025,
    marginTopRem: 1,
    marginBottomRem: 1,
  },
  subheadline: {
    sizeScale: 1,
    lineHeight: 1.36,
    letterSpacingEm: -0.01,
    marginTopRem: 1,
    marginBottomRem: 1,
  },
  body: {
    sizeScale: 1,
    lineHeight: 1.72,
    letterSpacingEm: 0,
    marginTopRem: 1,
    marginBottomRem: 1,
  },
  caption: {
    sizeScale: 1,
    lineHeight: 1.58,
    letterSpacingEm: 0.01,
    marginTopRem: 1,
    marginBottomRem: 1,
  },
  label: {
    sizeScale: 1,
    lineHeight: 1.42,
    letterSpacingEm: 0.26,
    marginTopRem: 1,
    marginBottomRem: 1,
  },
};

/** User-defined text variant (saved under `theme.typography.customVariants`). */
export type CustomTextVariantDefinition = ThemeTypographyVariantTuning & {
  id: string;
  name: string;
  /**
   * Built-in role whose font stack and fluid clamp shape this variant follows.
   * Metrics on this object override the defaults for that template.
   */
  template: ThemeTypographyRole;
};

const MAX_CUSTOM_TEXT_VARIANTS = 24;

export function isBuiltinTypographyVariantId(id: string): id is ThemeTypographyRole {
  return (THEME_TYPOGRAPHY_ROLES as readonly string[]).includes(id);
}

/** Lowercase slug; rejects empty, too long, or ids that collide with built-in role names. */
export function sanitizeCustomTextVariantId(raw: string): string | null {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  if (s.length < 1 || s.length > 48) return null;
  if (isBuiltinTypographyVariantId(s)) return null;
  return s;
}

function ensureUniqueCustomVariantId(baseId: string, used: Set<string>): string {
  if (!used.has(baseId)) return baseId;
  let n = 2;
  while (used.has(`${baseId}-${n}`)) n += 1;
  return `${baseId}-${n}`;
}

function normalizeCustomTextVariantCell(
  cell: unknown,
  index: number,
  usedIds: Set<string>
): CustomTextVariantDefinition | null {
  if (!cell || typeof cell !== 'object' || Array.isArray(cell)) return null;
  const o = cell as Record<string, unknown>;
  const nameRaw = o.name;
  const name =
    typeof nameRaw === 'string' && nameRaw.trim() !== '' ? nameRaw.trim() : `Custom variant ${index + 1}`;
  const fromId = typeof o.id === 'string' ? sanitizeCustomTextVariantId(o.id) : null;
  const fromName = sanitizeCustomTextVariantId(name);
  let baseId = fromId ?? fromName ?? `variant-${index + 1}`;
  baseId = ensureUniqueCustomVariantId(baseId, usedIds);
  usedIds.add(baseId);

  const tpl = o.template;
  const template: ThemeTypographyRole =
    typeof tpl === 'string' && (THEME_TYPOGRAPHY_ROLES as readonly string[]).includes(tpl)
      ? (tpl as ThemeTypographyRole)
      : 'body';

  const metrics: ThemeTypographyVariantTuning = { ...DEFAULT_THEME_TYPOGRAPHY_VARIANTS[template] };
  mergeVariantRole(metrics, o);

  return {
    id: baseId,
    name,
    template,
    sizeScale: metrics.sizeScale,
    lineHeight: metrics.lineHeight,
    letterSpacingEm: metrics.letterSpacingEm,
    marginTopRem: metrics.marginTopRem,
    marginBottomRem: metrics.marginBottomRem,
  };
}

function extractCustomVariantsFromTypography(raw: unknown): CustomTextVariantDefinition[] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const o = raw as Record<string, unknown>;
  const arr = o.customVariants;
  if (!Array.isArray(arr)) return [];
  const used = new Set<string>();
  const out: CustomTextVariantDefinition[] = [];
  for (let i = 0; i < arr.length && out.length < MAX_CUSTOM_TEXT_VARIANTS; i += 1) {
    const def = normalizeCustomTextVariantCell(arr[i], i, used);
    if (def) out.push(def);
  }
  return out;
}

export function getCustomTextVariantById(
  list: CustomTextVariantDefinition[],
  id: string
): CustomTextVariantDefinition | undefined {
  return list.find((v) => v.id === id);
}

export function themeTypographyHeadlineScaleMultiplier(headlineScale: ThemeHeadlineScale): number {
  if (headlineScale === 'large') return 1.16;
  if (headlineScale === 'xl') return 1.32;
  return 1;
}

/**
 * Inline typography for a custom variant (fluid clamp aligned with SectionComponentRenderer.css base rules).
 */
export function buildCustomTextVariantInnerStyle(
  def: CustomTextVariantDefinition,
  options: {
    fontFamily: string;
    headlineScaleMultiplier: number;
    labelUppercase: boolean;
  }
): Record<string, string | number> {
  const sz = def.sizeScale;
  const hs = options.headlineScaleMultiplier;
  const fontFamily = options.fontFamily;
  const letterSpacing = `calc(${def.letterSpacingEm} * 1em)`;

  const commonWordWrap = {
    maxWidth: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    margin: 0,
    color: 'inherit',
  } as const;

  if (def.template === 'headline') {
    return {
      fontFamily,
      fontSize: `clamp(calc(2.35rem * ${hs} * ${sz}), calc((3.2vw + 1.4rem) * ${hs} * ${sz}), calc(4rem * ${hs} * ${sz}))`,
      fontWeight: 300,
      lineHeight: def.lineHeight,
      letterSpacing,
      ...commonWordWrap,
    };
  }
  if (def.template === 'subheadline') {
    return {
      fontFamily,
      fontSize: `clamp(calc(1.35rem * ${hs} * ${sz}), calc((1.35vw + 1.05rem) * ${hs} * ${sz}), calc(2.05rem * ${hs} * ${sz}))`,
      fontWeight: 400,
      lineHeight: def.lineHeight,
      letterSpacing,
      opacity: 0.92,
      ...commonWordWrap,
    };
  }
  if (def.template === 'body') {
    return {
      fontFamily,
      fontSize: `clamp(calc(1.06rem * ${sz}), calc((0.45vw + 0.95rem) * ${sz}), calc(1.2rem * ${sz}))`,
      fontWeight: 400,
      lineHeight: def.lineHeight,
      letterSpacing,
      ...commonWordWrap,
    };
  }
  if (def.template === 'caption') {
    return {
      fontFamily,
      fontSize: `clamp(calc(0.94rem * ${sz}), calc((0.35vw + 0.84rem) * ${sz}), calc(1.06rem * ${sz}))`,
      fontWeight: 400,
      lineHeight: def.lineHeight,
      letterSpacing,
      opacity: 0.82,
      ...commonWordWrap,
    };
  }
  // label
  return {
    fontFamily,
    fontSize: `clamp(calc(0.74rem * ${sz}), calc((0.28vw + 0.66rem) * ${sz}), calc(0.875rem * ${sz}))`,
    fontWeight: 500,
    lineHeight: def.lineHeight,
    letterSpacing,
    textTransform: options.labelUppercase ? 'uppercase' : 'none',
    opacity: 0.88,
    display: 'block',
    ...commonWordWrap,
  };
}

export type ThemeTypographyHeroTuning = {
  /** Extra multiplier on headline + subheadline in full-height / fluid hero sections. */
  headlineSubSizeScale: number;
  /** Scales body, caption, and label in those hero sections. */
  bodyCaptionLabelScale: number;
};

export const DEFAULT_THEME_TYPOGRAPHY_HERO: ThemeTypographyHeroTuning = {
  headlineSubSizeScale: 1,
  bodyCaptionLabelScale: 1,
};

/** Mark ids for inline substring styling: `[[a]]…[[/a]]`, `[[b]]…[[/b]]`, `[[c]]…[[/c]]`. */
export const TEXT_INLINE_MARK_IDS = ['a', 'b', 'c'] as const;
export type TextInlineMarkId = (typeof TEXT_INLINE_MARK_IDS)[number];

/** Accent mode: colour / italic / weight / size relative to the block’s text variant font stack. */
export type TextInlineMarkAccentKind = {
  kind: 'accent';
  fontStyle: 'normal' | 'italic';
  /** Size relative to surrounding text (`em` on the span). */
  sizeEm: number;
  /** Hex, CSS, or theme token; omit for theme accent gold. */
  color?: string;
  fontWeight: 'inherit' | 400 | 500 | 600 | 700;
};

/** Use another typography variant’s metrics (same list as Text → Variant) inside the line. */
export type TextInlineMarkVariantKind = {
  kind: 'variant';
  /** Built-in role id, or a custom variant id from theme typography. */
  textVariant: string;
  /** Multiplier on the variant’s resolved font size (default 1). */
  sizeEm: number;
  /** Optional colour override; omit to inherit the block’s text colour. */
  color?: string;
};

export type TextInlineMarkSlotConfig = TextInlineMarkAccentKind | TextInlineMarkVariantKind;

/** Per-mark overrides on Text / Image+text blocks. Omitted marks use defaults when their markers appear in copy. */
export type TextInlineMarksMap = Partial<Record<TextInlineMarkId, Partial<TextInlineMarkAccentKind | TextInlineMarkVariantKind>>>;

const DEFAULT_TEXT_INLINE_MARK_ACCENT: TextInlineMarkAccentKind = {
  kind: 'accent',
  fontStyle: 'italic',
  sizeEm: 1,
  fontWeight: 'inherit',
  color: undefined,
};

function clampMarkSizeEm(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.min(2, Math.max(0.5, n));
}

export function mergeTextInlineMarkSlot(
  _markId: TextInlineMarkId,
  partial: Partial<TextInlineMarkAccentKind | TextInlineMarkVariantKind> | undefined
): TextInlineMarkSlotConfig {
  if (partial && partial.kind === 'variant') {
    const tv = typeof partial.textVariant === 'string' && partial.textVariant.trim() !== '' ? partial.textVariant.trim() : 'body';
    return {
      kind: 'variant',
      textVariant: tv,
      sizeEm: clampMarkSizeEm(typeof partial.sizeEm === 'number' ? partial.sizeEm : 1),
      color: typeof partial.color === 'string' && partial.color.trim() !== '' ? partial.color.trim() : undefined,
    };
  }
  const p = partial as Partial<TextInlineMarkAccentKind> | undefined;
  const fw = p?.fontWeight;
  const fontWeight: TextInlineMarkAccentKind['fontWeight'] =
    fw === 400 || fw === 500 || fw === 600 || fw === 700 || fw === 'inherit' ? fw : DEFAULT_TEXT_INLINE_MARK_ACCENT.fontWeight;
  return {
    kind: 'accent',
    fontStyle: p?.fontStyle === 'normal' ? 'normal' : 'italic',
    sizeEm: clampMarkSizeEm(typeof p?.sizeEm === 'number' ? p.sizeEm : DEFAULT_TEXT_INLINE_MARK_ACCENT.sizeEm),
    color: typeof p?.color === 'string' && p.color.trim() !== '' ? p.color.trim() : undefined,
    fontWeight,
  };
}

/** Resolved CSS for one marked span (inline). */
export type ResolvedTextInlineMarkStyle = {
  fontFamily: string;
  fontStyle: 'normal' | 'italic';
  fontSize: string;
  /** Omitted to inherit (variant + no colour override, or accent weight inherit). */
  color?: string;
  fontWeight?: number;
  lineHeight?: string | number;
  letterSpacing?: string;
  textTransform?: string;
  opacity?: number;
  display?: 'inline';
};

/**
 * Font stack for a typography role from the current client theme (window config).
 * Safe when config is missing (returns default preset body/heading stack).
 */
export function getThemeTypographyFontStackForRole(role: ThemeTypographyRole): string {
  const cfg =
    typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null;
  const { roles: merged } = resolveThemeTypographyFromRaw(cfg?.theme?.typography);
  return fontFamilyForTypographyRole(role, merged[role]);
}

/**
 * Typography role whose font stack applies to this block variant (built-in id, or custom variant’s template).
 */
export function themeTypographyRoleForTextVariant(variant: string | undefined): ThemeTypographyRole {
  const v = (variant ?? 'body').trim() || 'body';
  if (isBuiltinTypographyVariantId(v)) return v;
  const cfg =
    typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null;
  const { customVariants } = resolveThemeTypographyFromRaw(cfg?.theme?.typography);
  return getCustomTextVariantById(customVariants, v)?.template ?? 'body';
}

function builtinRoleAsCustomVariantDefinition(
  role: ThemeTypographyRole,
  tuning: ThemeTypographyTuning
): CustomTextVariantDefinition {
  const m = tuning.variants[role];
  return {
    id: role,
    name: role,
    template: role,
    sizeScale: m.sizeScale,
    lineHeight: m.lineHeight,
    letterSpacingEm: m.letterSpacingEm,
    marginTopRem: m.marginTopRem,
    marginBottomRem: m.marginBottomRem,
  };
}

/**
 * CSS for one inline mark (accent tokens or another typography variant inside the block).
 */
export function resolveTextInlineMarkStyle(
  slot: TextInlineMarkSlotConfig,
  blockVariant: string | undefined
): ResolvedTextInlineMarkStyle {
  const cfg =
    typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null;
  const typo = resolveThemeTypographyFromRaw(cfg?.theme?.typography);
  const blockRole = themeTypographyRoleForTextVariant(blockVariant);

  if (slot.kind === 'accent') {
    const fontFamily = getThemeTypographyFontStackForRole(blockRole);
    const color =
      typeof slot.color === 'string' && slot.color.trim() !== ''
        ? (resolveColorValue(slot.color.trim()) ?? slot.color.trim())
        : 'var(--color-accent-gold, #d4af37)';
    const fontWeight = slot.fontWeight === 'inherit' ? undefined : slot.fontWeight;
    return {
      fontFamily,
      fontStyle: slot.fontStyle,
      fontSize: `calc(1em * ${slot.sizeEm})`,
      color,
      ...(fontWeight !== undefined ? { fontWeight } : {}),
      display: 'inline',
    };
  }

  const vId = slot.textVariant.trim() || 'body';
  const customDef = !isBuiltinTypographyVariantId(vId) ? getCustomTextVariantById(typo.customVariants, vId) : undefined;
  const def =
    customDef ?? builtinRoleAsCustomVariantDefinition(isBuiltinTypographyVariantId(vId) ? vId : 'body', typo.tuning);
  const inner = buildCustomTextVariantInnerStyle(def, {
    fontFamily: getThemeTypographyFontStackForRole(def.template),
    headlineScaleMultiplier: themeTypographyHeadlineScaleMultiplier(typo.tuning.headlineScale),
    labelUppercase: typo.tuning.labelUppercase,
  });
  const baseFs = String(inner.fontSize ?? '1em');
  const fontSize = slot.sizeEm !== 1 ? `calc(${baseFs} * ${slot.sizeEm})` : baseFs;
  const color =
    typeof slot.color === 'string' && slot.color.trim() !== ''
      ? (resolveColorValue(slot.color.trim()) ?? slot.color.trim())
      : undefined;
  const fontWeight = typeof inner.fontWeight === 'number' ? inner.fontWeight : undefined;
  return {
    fontFamily: String(inner.fontFamily),
    fontStyle: 'normal',
    fontSize,
    ...(color !== undefined ? { color } : {}),
    ...(fontWeight !== undefined ? { fontWeight } : {}),
    ...(inner.lineHeight !== undefined ? { lineHeight: inner.lineHeight } : {}),
    ...(inner.letterSpacing !== undefined ? { letterSpacing: String(inner.letterSpacing) } : {}),
    ...(inner.textTransform !== undefined ? { textTransform: String(inner.textTransform) } : {}),
    ...(typeof inner.opacity === 'number' ? { opacity: inner.opacity } : {}),
    display: 'inline',
  };
}

/** Resolved styles for every mark id (defaults when a slot has no saved config). */
export function resolveTextInlineMarksStyles(
  marks: TextInlineMarksMap | undefined,
  blockVariant: string | undefined
): Record<TextInlineMarkId, ResolvedTextInlineMarkStyle> {
  const out = {} as Record<TextInlineMarkId, ResolvedTextInlineMarkStyle>;
  for (const id of TEXT_INLINE_MARK_IDS) {
    out[id] = resolveTextInlineMarkStyle(mergeTextInlineMarkSlot(id, marks?.[id]), blockVariant);
  }
  return out;
}

/** Resolved typography tuning (always complete). */
export type ThemeTypographyTuning = {
  /** When true, Label variant uses uppercase. */
  labelUppercase: boolean;
  headlineScale: ThemeHeadlineScale;
  variants: Record<ThemeTypographyRole, ThemeTypographyVariantTuning>;
  hero: ThemeTypographyHeroTuning;
};

export function copyDefaultThemeTypographyTuning(): ThemeTypographyTuning {
  const variants = {} as Record<ThemeTypographyRole, ThemeTypographyVariantTuning>;
  for (const role of THEME_TYPOGRAPHY_ROLES) {
    variants[role] = { ...DEFAULT_THEME_TYPOGRAPHY_VARIANTS[role] };
  }
  return {
    labelUppercase: true,
    headlineScale: 'default',
    variants,
    hero: { ...DEFAULT_THEME_TYPOGRAPHY_HERO },
  };
}

export const DEFAULT_THEME_TYPOGRAPHY_TUNING: ThemeTypographyTuning = copyDefaultThemeTypographyTuning();

/** Firestore tuning fragment (partial). */
export type ThemeTypographyTuningFirestore = Partial<{
  labelUppercase: boolean;
  headlineScale: ThemeHeadlineScale;
  /** @deprecated Read once into `variants.label.letterSpacingEm` at extract. */
  labelLetterSpacingEm: number;
  variants: Partial<Record<ThemeTypographyRole, Partial<ThemeTypographyVariantTuning>>>;
  hero: Partial<ThemeTypographyHeroTuning>;
}>;

/** Nested Firestore shape (preferred). */
export type ThemeTypographyNested = {
  roles?: Partial<Record<ThemeTypographyRole, string>>;
  tuning?: ThemeTypographyTuningFirestore;
  /** Extra text variants selectable on Text / Image+text (ids must not match built-in role names). */
  customVariants?: CustomTextVariantDefinition[];
};

/**
 * Firestore value: either legacy flat `{ headline: "inter", ... }` or nested `{ roles, tuning }`.
 */
export type ThemeTypographyConfigInput = Partial<Record<ThemeTypographyRole, string>> | ThemeTypographyNested;

function clampTypographyEm(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function normalizeHeadlineScale(raw: unknown): ThemeHeadlineScale {
  if (raw === 'large' || raw === 'xl' || raw === 'default') return raw;
  return 'default';
}

function extractRolesPartialFromTypography(raw: unknown): Partial<Record<ThemeTypographyRole, string>> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const o = raw as Record<string, unknown>;
  if (o.roles && typeof o.roles === 'object' && !Array.isArray(o.roles)) {
    return o.roles as Partial<Record<ThemeTypographyRole, string>>;
  }
  const out: Partial<Record<ThemeTypographyRole, string>> = {};
  for (const role of THEME_TYPOGRAPHY_ROLES) {
    const v = o[role];
    if (typeof v === 'string') out[role] = v;
  }
  return out;
}

function clampTypographyNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function mergeVariantRole(target: ThemeTypographyVariantTuning, partial: Record<string, unknown>): void {
  if (typeof partial.sizeScale === 'number' && Number.isFinite(partial.sizeScale)) {
    target.sizeScale = clampTypographyNumber(partial.sizeScale, 0.55, 2);
  }
  if (typeof partial.lineHeight === 'number' && Number.isFinite(partial.lineHeight)) {
    target.lineHeight = clampTypographyNumber(partial.lineHeight, 1, 2.4);
  }
  if (typeof partial.letterSpacingEm === 'number' && Number.isFinite(partial.letterSpacingEm)) {
    target.letterSpacingEm = clampTypographyNumber(partial.letterSpacingEm, -0.08, 0.35);
  }
  if (typeof partial.marginTopRem === 'number' && Number.isFinite(partial.marginTopRem)) {
    target.marginTopRem = clampTypographyNumber(partial.marginTopRem, 0, 6);
  }
  if (typeof partial.marginBottomRem === 'number' && Number.isFinite(partial.marginBottomRem)) {
    target.marginBottomRem = clampTypographyNumber(partial.marginBottomRem, 0, 6);
  }
}

function mergeVariantsFragment(
  target: Record<ThemeTypographyRole, ThemeTypographyVariantTuning>,
  fragment: unknown
): void {
  if (!fragment || typeof fragment !== 'object' || Array.isArray(fragment)) return;
  const p = fragment as Record<string, unknown>;
  for (const role of THEME_TYPOGRAPHY_ROLES) {
    const cell = p[role];
    if (cell && typeof cell === 'object' && !Array.isArray(cell)) {
      mergeVariantRole(target[role], cell as Record<string, unknown>);
    }
  }
}

function mergeHeroFragment(target: ThemeTypographyHeroTuning, fragment: unknown): void {
  if (!fragment || typeof fragment !== 'object' || Array.isArray(fragment)) return;
  const h = fragment as Record<string, unknown>;
  if (typeof h.headlineSubSizeScale === 'number' && Number.isFinite(h.headlineSubSizeScale)) {
    target.headlineSubSizeScale = clampTypographyNumber(h.headlineSubSizeScale, 0.7, 1.6);
  }
  if (typeof h.bodyCaptionLabelScale === 'number' && Number.isFinite(h.bodyCaptionLabelScale)) {
    target.bodyCaptionLabelScale = clampTypographyNumber(h.bodyCaptionLabelScale, 0.7, 1.6);
  }
}

function mergeTuningFragment(target: ThemeTypographyTuning, fragment: Record<string, unknown>): void {
  if (typeof fragment.labelUppercase === 'boolean') {
    target.labelUppercase = fragment.labelUppercase;
  }
  if (typeof fragment.headlineScale === 'string') {
    target.headlineScale = normalizeHeadlineScale(fragment.headlineScale);
  }
  if (typeof fragment.labelLetterSpacingEm === 'number' && Number.isFinite(fragment.labelLetterSpacingEm)) {
    target.variants.label.letterSpacingEm = clampTypographyEm(fragment.labelLetterSpacingEm, 0.06, 0.26);
  }
  if (fragment.variants) mergeVariantsFragment(target.variants, fragment.variants);
  if (fragment.hero) mergeHeroFragment(target.hero, fragment.hero);
}

function extractTuningFromTypography(raw: unknown): ThemeTypographyTuning {
  const tuning = copyDefaultThemeTypographyTuning();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return tuning;
  const o = raw as Record<string, unknown>;
  const nested = o.tuning && typeof o.tuning === 'object' && !Array.isArray(o.tuning) ? (o.tuning as Record<string, unknown>) : null;
  mergeTuningFragment(tuning, o as Record<string, unknown>);
  if (nested) mergeTuningFragment(tuning, nested);
  return tuning;
}

/**
 * Normalizes Firestore typography (flat role map or `{ roles, tuning }`) into merged preset ids + tuning defaults.
 */
export function resolveThemeTypographyFromRaw(raw: unknown): {
  roles: Record<ThemeTypographyRole, string>;
  tuning: ThemeTypographyTuning;
  customVariants: CustomTextVariantDefinition[];
} {
  const roles = getMergedThemeTypographyPresetIds(extractRolesPartialFromTypography(raw));
  const tuning = extractTuningFromTypography(raw);
  const customVariants = extractCustomVariantsFromTypography(raw);
  return { roles, tuning, customVariants };
}

export interface ClientConfig {
  clientId: string;
  /**
   * Lowercase emails that may request the admin login "Email link" for this client, in addition to
   * CMC admins and users who already have a role with Access admin. Does not grant dashboard access by itself.
   */
  adminEmailLinkAllowlist?: string[];
  /**
   * E.164 phone numbers that may use admin login "Phone" (SMS OTP) for this client, in addition to
   * CMC admins and users who already have Access admin. Stored normalized (e.g. +18185551212).
   */
  adminPhoneLinkAllowlist?: string[];
  businessName: string;
  siteUrl?: string; // Custom domain URL (e.g., "https://custom-domain.com")
  contact: {
    email: string;
    phone?: string;
    address?: string;
  };
  branding: {
    logo: string;
    title: string;
    metaDescription: string; // SEO meta description
    heroImage?: string;
    ogImage?: string; // Optional image URL for social sharing (Open Graph, Twitter Card)
    /** Browser tab icon (favicon). URL or storage path like logo. When set, it wins over the attribution logo. When unset and CMC attribution is on with a logo, that logo is used for the tab icon. */
    favicon?: string;
    /** When false, hide the site title text in the navbar (browser tab title is unchanged). Default true. */
    showTitleInNavbar?: boolean;
  };
  /** CMC attribution shown on client site (e.g. "Powered by Unwonted Artistry"). Set in CMC admin only. */
  poweredBy?: {
    show: boolean;
    text: string;
    logoUrl?: string;
    linkUrl: string;
    linkLabel?: string;
    secondaryLinks?: { label: string; url: string }[];
  };
  firebase: {
    projectId: string;
    stagingProjectId: string;
    storageBucket: string;
    stagingStorageBucket: string;
    authDomain: string;
  };
  /**
   * reCAPTCHA v3 site key for Firebase App Check. When App Check enforcement is enabled for Cloud
   * Storage in the Firebase project, set this field on `clientConfigs/{clientId}` (same key as in
   * App Check → Register web app) so casting comp card and other public uploads receive a token.
   */
  appCheckRecaptchaV3SiteKey?: string;
  social: {
    instagram?: {
      handles: string[];
      embedScript: string;
    };
  };
  features: {
    admin: boolean;
    hero: boolean;
    bookings: boolean;
    contracts: boolean;
    sms: boolean;
    casting: boolean;
    availability: boolean;
    publications: boolean;
    shop: boolean;
    models: boolean;
    tax: boolean;
  };
  /**
   * Member publication deliverables: where uploads go (Firebase / Google Drive one-time setup).
   * Stored in `clientConfigs/{clientId}`; merged at runtime by bootstrap.
   */
  publicationHub?: {
    /**
     * Google Drive folder id (from the folder URL). Client shares this folder with the platform
     * service account email once; uploads are copied here from Firebase Storage via Cloud Function.
     */
    googleDriveDestinationFolderId?: string;
    /** When false, hide Firebase Storage upload in the member hub. Default true when omitted. */
    allowFirebaseStorageUploads?: boolean;
    /** When true, ready-for-review deliverables auto-accept into show media inventory. Default false. */
    autoAcceptDeliverablesIntoMedia?: boolean;
    /** Model gallery token TTL in days (Phase 3). Default 90. */
    modelGalleryTokenTtlDays?: number;
    /**
     * Default CC for publication upload invite emails (comma/semicolon/newline list). When unset, falls back to
     * {@link ClientConfig.contact.email} at send time.
     */
    submissionInviteDefaultCc?: string;
    /**
     * Content pool (or portfolio) data source for sponsors & partners — typically the same pool as the site logo strip.
     * Instagram handles from this source are appended to model gallery credits and package emails.
     */
    sponsorsPartnersDataSourceId?: string;
    /** Optional content-pool `data` key for partner name when auto-detect is not enough. */
    sponsorsPartnersNameField?: string;
    /** Optional content-pool `data` key for Instagram handle or profile URL. */
    sponsorsPartnersInstagramField?: string;
    /** Optional content-pool `data` key for portfolio / website URL. */
    sponsorsPartnersPortfolioField?: string;
    /**
     * Content pool (or portfolio) data source for production team tags — typically the site team section pool.
     * Instagram handles merge into the Production line on model show captions.
     */
    teamDataSourceId?: string;
    teamNameField?: string;
    teamInstagramField?: string;
    teamPortfolioField?: string;
  };
  /**
   * Casting hub / line-up settings stored on `clientConfigs/{clientId}`.
   * Set `lineupMusicMaxBytes` to override the default 100 MiB upload cap (platform max 200 MiB).
   */
  castingHub?: {
    lineupMusicMaxBytes?: number;
  };
  /**
   * First-party site activity (anonymous + signed-in visits) for Members → Site activity.
   * When `enabled` is false, the client stops recording (Admin cost control). Default true when omitted.
   */
  siteActivity?: {
    enabled?: boolean;
  };
  /**
   * Members-area hub (`/members`): tool order, hub visibility, and optional per-tool role allowlists.
   * Edited in Admin → User management → Members hub. Stored in `clientConfigs/{clientId}`.
   */
  membersArea?: MembersAreaHubOverlay;
  /** Booking payment pass-through fees and admin pricing calculator defaults. */
  bookings?: import('../utils/processingFeeConfig').BookingsSettings;
  contract: {
    defaultTimeline: string;
    governingLaw: string;
    /** Applied to volunteer & event collaborator agreements; issuance date is set when each HTML copy is generated or emailed. */
    producerSignature?: ProducerSignatureConfig;
    /** Admin-only: reusable wizard field values applied across contract types (Contract Management). */
    wizardCommonDefaults?: Record<string, string>;
    /**
     * Site-wide fallback for the business's contract representative — the human named on the
     * "Business, represented by ___" line. Used when the booking has no matching provider in
     * `serviceProviders[]`. Solo operators usually set this once and never touch it again.
     */
    defaultRepresentativeName?: string;
    /**
     * Optional title appended after the representative's name (e.g. "Lead Photographer"). Renders
     * as `BUSINESS, represented by NAME, TITLE ("ROLE")`. When unset only the name renders.
     */
    defaultRepresentativeTitle?: string;
    /**
     * Site-wide fallback for the booking-contract heading (e.g. "CREATIVE SERVICES AGREEMENT").
     * Used when the matched service has no `contractTitle`. Empty falls back to the built-in
     * UGC vs. modeling default.
     */
    defaultContractTitle?: string;
    /**
     * Site-wide fallback for the booking-contract subtitle paired with {@link defaultContractTitle}.
     * Renders under the title block.
     */
    defaultContractSubtitle?: string;
    /**
     * Default CC line for casting applicant emails (single + bulk messaging modals). Comma, semicolon, or newline separated.
     * When unset or blank, modals default CC to {@link ClientConfig.contact.email} when that is set.
     */
    castingApplicantCommsDefaultCc?: string;
    /** Optional backgrounds and text colors for agreement HTML previews and PDFs (no borders). */
    documentStyle?: ContractDocumentStyle;
    /**
     * Signing page iframe background: `site` (default) uses the live site theme for contrast;
     * `contract` uses {@link documentStyle} colors (for dark site themes with poor contrast).
     */
    signingPreviewBackground?: 'site' | 'contract';
    /**
     * Optional Firestore `contractTemplates` document id per event inquiry type.
     * When set, accept/preview loads that template (its `type` must match the agreement kind for that inquiry type).
     * Omitted types use the active template for that agreement kind (same as before this map existed).
     */
    eventInquiryTemplateIdByInquiryType?: Partial<Record<EventCoordinatorInquiryType, string>>;
  };
  sms: {
    templates: {
      bookingConfirmation: string;
      bookingReminder: string;
      optOutMessage: string;
    };
  };
  email?: {
    enabled?: boolean;
    provider?: 'smtp' | 'ses' | 'none';
    // Global email provider credentials
    ses?: {
      accessKeyId?: string;
      secretAccessKey?: string;
      region?: string;
      fromEmail?: string;
      fromName?: string;
    };
    smtp?: {
      host?: string;
      port?: number;
      secure?: boolean;
      auth?: {
        user: string;
        pass: string;
      };
      fromEmail?: string;
      fromName?: string;
    };
    replyTo?: string;
    // Feature-specific email settings
    features?: {
      bookings?: {
        enabled?: boolean;
        fromEmail?: string;
        templates?: {
          confirmation?: {
            subject?: string;
            body?: string;
          };
          reminder?: {
            subject?: string;
            body?: string;
          };
          statusUpdate?: {
            subject?: string;
            body?: string;
          };
        };
      };
      shop?: {
        enabled?: boolean;
        fromEmail?: string;
        templates?: {
          orderConfirmation?: {
            subject?: string;
            body?: string;
          };
          orderStatusUpdate?: {
            subject?: string;
            body?: string;
          };
        };
      };
      invoices?: {
        enabled?: boolean;
        fromEmail?: string;
        templates?: {
          invoiceSent?: {
            subject?: string;
            body?: string;
          };
        };
      };
      models?: {
        enabled?: boolean;
        fromEmail?: string;
        magicLinkExpirationHours?: number; // @deprecated Use signupTokenExpirationDays instead (24 hours = 1 day)
        signupTokenExpirationDays?: number; // Default: 7 days
        accountExpirationDays?: number; // Default: 7 days (time to set password before account deletion)
        templates?: {
          authLink?: {
            subject?: string;
            body?: string;
          };
          signupLink?: {
            subject?: string;
            body?: string;
          };
          /** Sent immediately after the model sets their password via the signup link. Placeholders: {{modelName}}, {{firstName}}, {{lastName}}, {{siteUrl}}, {{portfolioLoginUrl}}. */
          passwordSet?: {
            subject?: string;
            body?: string;
          };
          /** Second email after password set (welcome). Same placeholders plus {{portfolioUrl}}, {{membersContractsUrl}}. If a representation contract exists, a contracts paragraph is appended when using defaults. */
          welcomeAfterPassword?: {
            subject?: string;
            body?: string;
          };
        };
      };
      ambassadors?: {
        enabled?: boolean;
        fromEmail?: string;
        signupTokenExpirationDays?: number; // Default: 7 days (same as models)
        accountExpirationDays?: number; // Default: 7 days (time to set password before account deletion)
        templates?: {
          signupLink?: {
            subject?: string;
            body?: string;
          };
        };
      };
      subscribers?: {
        enabled?: boolean;
        fromEmail?: string;
        templates?: {
          welcomeEmail?: {
            subject?: string;
            body?: string;
          };
        };
      };
    };
    // @deprecated Legacy email structure - migrate to email.fromEmails structure
    fromEmails?: {
      bookings?: string;
    };
    templates?: {
      bookingConfirmation?: {
        subject?: string;
        body?: string;
      };
      inquiryAgreementSignedFollowUp?: {
        subject?: string;
        body?: string;
      };
      inquiryAgreementSignedFollowUpByInquiryType?: Record<
        string,
        { subject?: string; body?: string }
      >;
    };
  };
  subscriptions?: {
    [feature: string]: {
      active: boolean;
      subscriptionId: string | null;
      status: 'inactive' | 'active' | 'canceled' | 'past_due' | 'trialing';
      plan: 'monthly' | 'yearly' | null;
      currentPeriodEnd: string | null;
      cancelAtPeriodEnd: boolean;
      provider: string;
    };
  };
  oneTimePurchases?: {
    [feature: string]: {
      purchased: boolean;
      purchaseId: string | null;
      purchasedAt: string | null;
      price: number;
      provider: string;
    };
  };
  featuresDisabled?: {
    [feature: string]: boolean; // Admin override to disable features even if paid
  };
  featuresVisible?: {
    [feature: string]: boolean; // Opt-in visibility for features in development mode
  };
  /** Admin feature tab labels (name, description) - overrides hardcoded values when set in Firestore */
  adminFeatureLabels?: {
    [featureId: string]: { name?: string; description?: string };
  };
  /** Config-driven routing: admin path and optional overrides. Default admin path is '/admin'. */
  routing?: {
    adminPath?: string;  // default '/admin'; invite links use adminPath?inviteToken= (same route, no extra path)
  };
  navigation?: {
    links?: NavLink[];
    /** When false, the site navigation bar is hidden. Default true. */
    showNavigation?: boolean;
    /** Path patterns where the navbar is hidden even when showNavigation is true. Exact paths and * wildcards (prefix or mid-path). */
    hideNavPathPatterns?: string[];
    /** When false, the light/dark mode toggle is hidden in the nav bar. Default true. */
    showThemeToggle?: boolean;
    /** When true, the nav bar hides when the user scrolls down and reappears when scrolling up. Default false (always visible). */
    hideNavOnScrollDown?: boolean;
    /** Navbar background opacity 0–1. Omit = 1. */
    alpha?: number;
    /** When true, the fixed nav bar on top of the page is shown. Omit = true. */
    navBarOnTop?: boolean;
    /** When true, the nav bar in the page flow (sibling to content) is shown. Omit = false. */
    navBarOnPage?: boolean;
    /** Navbar height in pixels. Omit = 80. */
    heightPx?: number;
    /** Navbar background colour (CSS value, e.g. #fff or rgba(0,0,0,0.9)). When set, overrides default bar colour. */
    backgroundColor?: string;
    /** Navbar text/link colour (CSS value). When set, overrides theme default for nav text. */
    textColor?: string;
    /**
     * Optional background for `.app-content` (site shell). When set, overrides theme `--color-bg` for that wrapper.
     * Visible at the top when the nav bar backdrop is transparent, and as the default canvas behind pages.
     */
    appContentBackgroundColor?: string;
    /**
     * Surface for platform legal pages (privacy, terms, disclaimer).
     * `light` = white background + dark text (default, always legible).
     * `theme` = follow site theme colours.
     */
    legalPagesSurface?: 'light' | 'theme';
    /** Style of the border/divider between navbar and hero section. */
    heroBorderStyle?: 'solid' | 'zigzag' | 'drippy' | 'waves' | 'slanted' | 'none';
    /**
     * Legacy: was used to inject a Press / volunteers nav item. Ignored for navbar rendering; add
     * `/event-inquiry/{eventId}` (or any route) as a normal entry under `links` instead.
     */
    eventInquiryNav?: {
      eventId?: string;
      label?: string;
      visible?: boolean;
      order?: number;
    };
    /**
     * `classic` = logo left, links + theme on the right (default). `editorial` = brand column (logo + title + optional tagline),
     * link cluster toward center-right, optional primary CTA pill + theme on the far right.
     */
    navLayoutVariant?: 'classic' | 'editorial';
    /** Optional second line under the site title in the navbar (editorial layout). */
    brandTagline?: string;
    /** Optional pill CTA (e.g. Inquire) when `navLayoutVariant` is `editorial`. Omit or empty = no CTA. */
    primaryCta?: {
      label: string;
      route: string;
      external?: boolean;
      /** Default true when `primaryCta` is present. */
      visible?: boolean;
    };
    /**
     * Editorial layout only: show configured nav links as a horizontal row (`inline`) or behind a dropdown button (`dropdown`).
     * Omit or unknown = inline.
     */
    editorialNavLinksPresentation?: 'inline' | 'dropdown';
    /** Button label for the editorial nav dropdown; omit or empty uses the default ("Pages"). */
    editorialNavDropdownTriggerLabel?: string;
  };
  /** Theme colour library. When set, applied to CSS --color-{token} and available for reference-by-name in admin. */
  theme?: {
    colors?: Record<string, string>;
    /** Up to 3 custom colour combinations (name + library); managed under Layout → Colour library. */
    customColorSlots?: CustomColorSlot[];
    /**
     * Font presets per text role + optional tuning. Flat role keys or `{ roles, tuning }`.
     */
    typography?: ThemeTypographyConfigInput;
    /** Default shape + appearance per button style (primary, secondary, …). Section buttons omitting overrides use these. */
    buttonDefaults?: ThemeButtonDefaultsFirestore;
    /**
     * Named button variants (Layout → Buttons). Each maps to a built-in colour skin (`template`) plus default shape/appearance.
     * Stored as an array; use {@link extractCustomButtonVariantsFromTheme} when reading.
     */
    customButtonVariants?: CustomButtonVariantDefinition[];
  };
  layout?: {
    // Global settings (shared across all pages)
    sections?: PageSection[];                // Global sections library (reusable across pages)
    /** Reusable data sources for collection/publications etc. Single or mixed; filterable. */
    dataSources?: DataSourceDefinition[];

    // Page-specific settings
    pages?: {
      homepage?: HomepagePageConfig;
      portfolio?: PortfolioPageConfig;
      about?: AboutPageConfig;
      bookMe?: BookMePageConfig;
      [pageId: string]: GenericPageConfig | undefined;
    };
  };
  /**
   * Firestore `updatedAt` when present (Timestamp or plain `{ seconds, nanoseconds }` from reads).
   * Optional on partial in-memory merges; used e.g. for tab-icon cache-bust in SeoHead.
   */
  updatedAt?: { seconds: number; nanoseconds: number } | { toMillis: () => number };
}

export interface PageSectionReference {
  sectionId: string;           // Reference to layout.sections[].id
  enabled: boolean;
  order: number;
}

/** Filter for a single data source (e.g. portfolio section, or publications query). */
export interface DataSourceFilter {
  /** For type 'portfolio': section id (comp-card, digitals, runway, print, ugc). */
  section?: string;
  /** Optional: only active items. Default true for portfolio. */
  isActive?: boolean;
  /** Optional: restrict to a casting project (for "Featured models" / "Casting results" blocks). */
  castingProjectId?: string;
  /** Future: tags, date range, etc. */
  tags?: string[];
}

/** Parameterized browse routes under a data source hub (e.g. `/runway/nyfw`). */
export interface DataSourceSubpageConfig {
  enabled: boolean;
  /** Portfolio item field or content-pool data key matched by the first URL segment. */
  segmentField: string;
  /** Optional second URL segment field (e.g. portfolio `category` for show pages). */
  detailSegmentField?: string;
  /** `layout.pages` id whose `sectionIds` are the shared subpage template. */
  templatePageId: string;
}

/** Field type for content-pool item shape. Determines control in Add/Edit form (string → text, image → library + upload, etc.). */
export type ContentPoolFieldType =
  | 'string'
  | 'longText'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'url'
  | 'timestamp'
  | 'image';

/** Single choice for {@link ContentPoolItemField.enumOptions} (`value` stored on items, `label` in pickers). */
export interface ContentPoolEnumOption {
  value: string;
  label: string;
  /** Shown on section tab bar when horizontal space is tight. */
  emoji?: string;
  shortLabel?: string;
}

/** Canonical list for validation and admin dropdowns (order = suggested UX order). */
export const CONTENT_POOL_FIELD_TYPES_LIST: readonly ContentPoolFieldType[] = [
  'string',
  'longText',
  'number',
  'boolean',
  'enum',
  'url',
  'timestamp',
  'image',
];

export function isContentPoolFieldType(x: string): x is ContentPoolFieldType {
  return (CONTENT_POOL_FIELD_TYPES_LIST as readonly string[]).includes(x);
}

/** Field definition for content-pool item shape (one object type, many items). Type is required; no backward compat. */
export interface ContentPoolItemField {
  key: string;
  label: string;
  type: ContentPoolFieldType;
  /** Required when `type` is `enum`: allowed stored values and display labels. */
  enumOptions?: ContentPoolEnumOption[];
}

/** Single backing store + filter. Reusable across components. */
export interface DataSourceDefinition {
  id: string;                  // Unique id (e.g. 'runway', 'print', 'publications-main', 'testimonials')
  label: string;               // Display name (e.g. 'Runway', 'Print', 'Publications')
  type: 'portfolio' | 'publications' | 'content-pool';
  filter?: DataSourceFilter;
  /** Public browse URL for this source (e.g. `/runway`, `/editorial`). Resolved automatically when omitted. */
  route?: string;
  /** For type 'content-pool': optional Firestore path. Default: contentPools/{clientId}/pools/{id}/items */
  collectionPath?: string;
  /** For type 'content-pool': required item shape (key, label, type per field). Add/Edit form is type-driven. */
  itemFields?: ContentPoolItemField[];
  /**
   * For type 'content-pool': capabilities pre-selected on new items (and merged into sample seeds).
   * Set automatically when creating from a field preset (e.g. publications-press).
   */
  defaultItemCapabilities?: Capability[];
  /**
   * For content pools with logoWhite + logoBlack image fields: which wordmark Logo Strip
   * (and admin thumbs) use by default. Omit → black. Override per component via field mapping
   * or Logo Strip “Force black/white”.
   */
  defaultLogoVariant?: 'black' | 'white';
  /** Optional parameterized sub-routes + section template for hub → subpage browsing. */
  subpages?: DataSourceSubpageConfig;
}

/** Mixed source: multiple sources combined (future). */
export interface MixedDataSourceDefinition {
  id: string;
  label: string;
  type: 'mixed';
  sources: Array<{ sourceId: string; order?: number }>;
  order?: 'append' | 'interleaved';
}

export interface HomepagePageConfig {
  path?: string;                             // URL path for this page (default '/')
  hero?: {
    desktop?: HeroLayout;
    mobile?: HeroLayout;
  };
  sections?: HomepageSection[];
  sectionIds?: PageSectionReference[];       // References to global sections
  componentMappings?: ComponentMapping[];    // Page-specific button/component mappings
  layout?: PageLayoutConfig;
  updatedAt?: Date;
}

export interface PortfolioPageConfig {
  path?: string;                               // URL path (default '/portfolio')
  collections?: PortfolioCollection[];         // Built-in portfolio collections
  sectionIds?: PageSectionReference[];         // References to global sections
  componentMappings?: ComponentMapping[];      // Page-specific button/component mappings
  layout?: PageLayoutConfig;
  updatedAt?: Date;
}

export interface PortfolioCollection {
  id: string;           // 'comp-card', 'digitals', 'runway', 'print', 'ugc'
  enabled: boolean;
  order: number;
}

export interface AboutPageConfig {
  path?: string;                              // URL path (default '/about')
  sections?: AboutSection[];                  // Built-in about sections
  customSections?: PageSection[];             // @deprecated Use sectionIds instead
  sectionIds?: PageSectionReference[];        // References to global sections
  componentMappings?: ComponentMapping[];     // Page-specific button/component mappings
  layout?: PageLayoutConfig;
  updatedAt?: Date;
}

export interface AboutSection {
  id: string;           // 'story', 'approach', 'services', 'equipment'
  enabled: boolean;
  order: number;
  title: string;
  content: string;      // Rich text content
  imageUrl?: string;
}

export interface BookMePageConfig {
  path?: string;                              // URL path (default '/book-me')
  form?: BookMeFormConfig;
  sections?: PageSection[];                   // @deprecated Use sectionIds instead
  sectionIds?: PageSectionReference[];        // References to global sections
  componentMappings?: ComponentMapping[];     // Page-specific button/component mappings
  layout?: PageLayoutConfig;
  updatedAt?: Date;
}

export interface BookMeFormConfig {
  fields?: FormField[];
  groups?: FormFieldGroup[];
}

export interface FormField {
  id: string;
  type: 'text' | 'email' | 'phone' | 'date' | 'time' | 'select' | 'textarea' | 'file' | 'number';
  label: string;
  placeholder?: string;
  required: boolean;
  order: number;
  groupId?: string;     // Optional: belongs to a field group
  conditional?: {
    fieldId: string;    // Field to watch
    operator: 'equals' | 'notEquals' | 'contains' | 'greaterThan' | 'lessThan';
    value: any;         // Value to compare
  };
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
  options?: string[];   // For select fields
}

export interface FormFieldGroup {
  id: string;
  label: string;
  order: number;
  collapsible?: boolean;
  collapsed?: boolean;
}

export interface PageLayoutConfig {
  desktop?: {
    // Desktop-specific layout settings
    [key: string]: any;
  };
  mobile?: {
    // Mobile-specific layout settings
    [key: string]: any;
  };
}

/** Page-level background (layer behind sections). */
export interface PageBackgroundConfig {
  type: 'color' | 'image';
  value?: string;           // Hex color or image URL/path
  overlay?: boolean;        // Dark overlay on image (ignored for color)
  overlayOpacity?: number;  // 0–1 for image overlay
  overlayColor?: string;   // Overlay colour (hex). Default #000000.
}

export interface GenericPageConfig {
  name?: string;                              // Page display name
  slug?: string;                              // URL slug (used as path if path not set)
  path?: string;                              // URL path (overrides slug; default from slug with leading /)
  icon?: string;                              // Icon for display
  layout?: PageLayoutConfig;
  sections?: GenericSection[];
  customSections?: PageSection[];             // Custom sections
  /** Page-level background (behind all sections). Enables layered backgrounds: page → section → content. */
  background?: PageBackgroundConfig;
  /** Padding (px) from page edges so section backgrounds sit inside the page frame (e.g. when page background has borders). 0 = edge-to-edge. */
  contentInsetPx?: number;
  /** When true, only signed-in users can view this page. Set in admin page editor. */
  requiresAuth?: boolean;
  /** When requiresAuth is true, optional permission key (e.g. canAccessMembersArea). User must have this permission. */
  requiredPermission?: string;
  /** When true, visitors see an under-construction placeholder instead of page sections. Admins still see the full page. */
  underConstruction?: boolean;
  /** Ordered references to global `layout.sections` (generic pages). */
  sectionIds?: PageSectionReference[];
  /** Optional per-segment section overrides when this page is a subpage template. */
  variants?: Record<string, { name?: string; sectionIds?: PageSectionReference[] }>;
  updatedAt?: Date;
}

export interface GenericSection {
  id: string;
  enabled: boolean;
  order: number;
  [key: string]: any;  // Flexible for page-specific section data
}

/** Sub-section background: color, image, or video. Omit for no background. */
export interface SubSectionBackground {
  type: 'color' | 'image' | 'video';
  /** For color: hex or theme token. For image: URL. Ignored for video. */
  value?: string;
  /** Optional mobile-specific image URL (falls back to `value`). */
  mobileImageUrl?: string;
  /** Video URL. Only used when type === 'video'. */
  videoUrl?: string;
  /** Poster image URL for video. Optional. */
  posterUrl?: string;
  /**
   * How the image fills the column. `cover` (default) fills and may crop.
   * `contain` shows the entire photo (letterbox if the column aspect differs).
   */
  imageFit?: 'cover' | 'contain';
  /** Background focus point per breakpoint. Optional cropArea stores zoomed region from crop editor. */
  focusDesktop?: ImageFocusPoint;
  focusMobile?: ImageFocusPoint;
}

/**
 * Soft horizontal fade on one edge of a sub-section into a sibling colour.
 * Typical use: photo column blends left into a white text column on split heroes.
 */
export interface SubSectionColumnBlend {
  /** Which edge of this column receives the fade. */
  side: 'left' | 'right';
  /** Colour to fade toward (usually the sibling column surface). Hex or theme token. */
  color: string;
  /** Fade width in px. */
  widthPx: number;
  /** Overall strength 0–1. Omitted = 1. */
  opacity?: number;
}

/** Sub-section: a region inside a section with its own layout. Components in a sub-section stack (no free positioning). */
export interface SubSection {
  id: string;
  /** How content stacks inside this sub-section: vertical = top-to-bottom, horizontal/diagonal for future use. */
  layout: 'vertical' | 'horizontal' | 'diagonal';
  /** Alignment of stacked content. Default center. */
  alignment?: 'left' | 'center' | 'right';
  /**
   * Main-axis placement of the component stack inside this sub-section when the column has extra height
   * (e.g. full-viewport heroes). Vertical layout: top / middle / bottom. Horizontal layout: start / center / end along the row.
   * Omitted = start (top or leading edge).
   */
  contentJustify?: 'start' | 'center' | 'end';
  /** Gap between stacked components in px. Omitted = 16 (1rem). Use smaller (e.g. 4, 8) for tighter text blocks. */
  componentGap?: number;
  /** Component ids in display order (must reference section.components). */
  componentIds: string[];
  /**
   * When section tabs are enabled: which tab panel owns this sub-section (`enum` value or `__all__`).
   * Required for tabbed sections — all content must live in a tab panel.
   */
  tabKey?: string;
  /**
   * Optional source linkage metadata when this sub-section was imported from an existing section.
   * `copy` means detached clone. `reference` means intentionally linked provenance for admin workflows.
   */
  sourceSectionLink?: SubSectionSourceSectionLink;
  /** Optional background for this sub-section (color, image, or video). */
  background?: SubSectionBackground;
  /**
   * Soft fade on the left or right edge into a sibling colour (split heroes).
   * Hidden automatically when mobile stacks columns vertically.
   */
  columnBlend?: SubSectionColumnBlend;
  /**
   * When true, draws a thin frame around this sub-section (1px border, radius, inner padding).
   * Use for editorial / card-style columns or stacked blocks. Default: off (legacy sections unchanged).
   */
  showBorder?: boolean;
  /** Inner padding inside the card frame (px) when showBorder is true. Omitted = theme default (24px). */
  borderPadding?: number;
  /**
   * Corner radius for the sub-section card frame when showBorder is true.
   * CSS length (e.g. `12px`, `0.75rem`, `9999px`). Sets `--page-subsection-border-radius` for this column.
   * Omitted = theme default (`--page-subsection-border-radius` from site/theme CSS).
   */
  borderRadius?: string;
}

/** Sentinel value for the optional "all items" tab (no enum filter). */
export const SECTION_TAB_ALL_VALUE = '__all__';
/** Components with this tabKey render above the tab bar (shared hero / collage), not in a tab panel. */
export const SECTION_TAB_ABOVE_TABS_KEY = '__above_tabs__';

export type SectionTabPosition = 'top' | 'bottom' | 'left' | 'right';

export type SectionTabSort = 'newest' | 'oldest' | 'label-asc';

/** One curated tab (from content-pool enum or synthetic all-tab). */
export interface SectionTabEntry {
  value: string;
  label: string;
  emoji?: string;
  shortLabel?: string;
}

export interface SectionTabsSharedHeader {
  enabled: boolean;
  showTitle: boolean;
  title?: string;
  showIntroText: boolean;
  introText?: string;
  showSort: boolean;
  sortField?: string;
  defaultSort?: SectionTabSort;
}

/** Optional logo-collage band above the tab bar (does not replace tab panels). */
export interface SectionTabsLogoCollageHero {
  /** Master toggle — off by default so existing tabbed sections stay unchanged. */
  enabled: boolean;
  /** Max unique logos in the collage (default 10). 0 = all. */
  maxVisible?: number;
  /**
   * Logo colour. Default `adapt` on the editorial light card (black + brand colour marks).
   */
  logoFilter?: 'adapt' | 'none' | 'grayscale' | 'monochrome-light' | 'monochrome-dark';
  /** Field used to dedupe logos (default `publication`). */
  uniqueByField?: string;
  /** Page identity inside the collage card (default PUBLICATIONS). */
  title?: string;
  /** Short line under the title (e.g. Press & editorial). */
  subtitle?: string;
  /** When false, hide the title block inside the card. Default true. */
  showTitle?: boolean;
}

export interface SectionTabsConfig {
  enabled: boolean;
  position: SectionTabPosition;
  dataSourceId: string;
  /** Content-pool enum field key used to filter items per tab. */
  tabField: string;
  /** Curated tabs in display order (drag-reorderable in admin). */
  tabs: SectionTabEntry[];
  /** Optional "all items" tab — typically first; omit to hide. */
  allTab?: SectionTabEntry | null;
  defaultTab?: string;
  /** Query param key for URL sync (default `tab`). */
  urlQueryKey?: string;
  /** Sync active tab to `?{urlQueryKey}=value` (default true when enabled). */
  syncTabToUrl?: boolean;
  /** When true (default), public site hides tabs with zero items. Admin preview always shows counts. */
  hideEmptyTabs?: boolean;
  sharedHeader?: SectionTabsSharedHeader;
  /** When enabled, shows a logo collage above tabs from this section’s data source. */
  logoCollageHero?: SectionTabsLogoCollageHero;
}

/** Provenance / sync metadata when a sub-section was imported or copied from another section column. */
export interface SubSectionSourceSectionLink {
  sectionId: string;
  /** Source sub-section column id when link is column-scoped (preferred for move/sync). */
  subSectionId?: string;
  mode: 'copy' | 'reference';
  sourceName?: string;
  /** ISO timestamp of last successful sync from source (linked copies). */
  lastSyncedAt?: string;
  /** Manual sync is default; auto-sync is opt-in (Phase 4). */
  syncPolicy?: 'manual' | 'auto';
  /** Content fingerprint at last sync — used for stale badge. */
  syncedContentFingerprint?: string;
}

// Generic page section interface (reusable across pages)
export interface PageSection {
  id: string;                    // Unique identifier (e.g., "custom-1")
  type: 'custom';                // Section type identifier
  enabled: boolean;              // Visibility toggle
  order: number;                 // Display order
  name?: string;                 // Optional readable name for admin list (e.g., "Services Section", "Hero Banner")
  title?: string;                // Optional section title displayed on the page
  layout: {
    width: 'full' | 'grid';      // Full width or within grid
    height?: 'full' | 'half' | 'auto' | 'fixed-full' | 'fixed-half' | 'scroll';    // scroll = max height + overflow
    /** When height === 'scroll', max height for the section (e.g. '70vh', '600px'). */
    scrollMaxHeight?: string;
    contentDisplay?: 'contained' | 'full-width' | 'edge-to-edge'; // How content is displayed within section
    sectionSpacing?: boolean;    // Whether to add spacing between sections and contain overflow (default: false)
    contentPadding?: number;     // Content padding in pixels (default: 16px = 1rem)
    /**
     * When true, skip the default mobile side gutter on edge-to-edge sections
     * (true full-bleed heroes / media bands). Default false — phones get --public-page-gutter.
     */
    fullBleedMobile?: boolean;
    /** @deprecated Read boundary only — use `motion`. New writes use `motion`. */
    reveal?: import('../motion/types').SectionRevealPreset;
    /** Section motion orchestration (stagger timeline, section entrance, etc.). */
    motion?: import('../motion/types').SectionMotionConfig;
    /** How sub-sections are arranged: horizontal = columns side-by-side, vertical = rows stacked. Omitted when no subSections. */
    subSectionsLayout?: 'horizontal' | 'vertical';
    /** Optional weights for each sub-section (same order as subSections). Space is split by ratio, e.g. [1, 2] = 1:2, [40, 60] = 40%/60%. Omitted = equal split. */
    subSectionWeights?: number[];
    /**
     * Gap between sibling sub-sections in pixels (columns when horizontal, rows when vertical).
     * Omitted = theme default from layout CSS (currently 40px).
     */
    subSectionGapPx?: number;
    /**
     * Mobile-only sub-section arrangement when `subSectionsLayout` is horizontal.
     * `stack` (default) = full-width vertical stack; `scroll` = horizontal thumb strip;
     * `carousel` = one slide with dots; `row` = stay side-by-side with shrunk columns.
     */
    mobileSubSectionsMode?: 'stack' | 'scroll' | 'carousel' | 'row';
    /**
     * Where unassigned section components render relative to sub-section columns/rows.
     * `below` = after sub-sections (default). `above` = before sub-sections.
     */
    subSectionUnassignedPlacement?: 'below' | 'above';
  };
  /** When set, section content is split into sub-sections; each sub-section stacks its components (no free positioning). Omitted = single flow (legacy). */
  subSections?: SubSection[];
  background: {
    type: 'none' | 'color' | 'image' | 'video';  // Background type; 'none' = no background
    value?: string;                      // Color hex code or image URL/Storage path (optional for video)
    videoUrl?: string;                   // Video URL for video backgrounds
    posterUrl?: string;                  // Poster image for video backgrounds
    overlay?: boolean;                   // Optional: dark overlay on image/video
    overlayOpacity?: number;             // 0-1 for overlay opacity
    overlayColor?: string;               // Overlay colour (hex). Default #000000.
    mobileImageUrl?: string;             // Optional mobile-specific background image
    mobileBackgroundPosition?: number;   // Horizontal position (0-100) for desktop image crop on mobile when no mobile image
    /** Gradient overlay between background and content (angle + color stops with alpha). */
    gradientOverlay?: {
      type: 'linear' | 'radial';
      angle?: number;                     // Degrees for linear (0 = to top, 90 = to right)
      stops: Array<{ position: number; color: string; opacity: number }>; // position 0-100, color hex, opacity 0-1
    };
    /** CSS mix-blend-mode for gradient overlay (e.g. multiply, overlay, soft-light). */
    gradientOverlayBlendMode?: 'normal' | 'multiply' | 'overlay' | 'soft-light';
    /** When true, background image uses fixed attachment for subtle parallax on scroll. */
    parallax?: boolean;
    /** Section shape: rectangular (default), curved-bottom, curved-top, angled-bottom, angled-top. */
    shape?: 'rectangular' | 'curved-bottom' | 'curved-top' | 'angled-bottom' | 'angled-top';
    /** Optional divider below section: color (hex) and thickness (px). */
    divider?: { color?: string; thickness?: number };
    /** Edge blend: gradient at top/bottom to blend into adjacent section colour. Height in px. */
    edgeBlend?: {
      top?: { color: string; heightPx: number };
      bottom?: { color: string; heightPx: number };
    };
    /** Background focus point per breakpoint. Optional cropArea stores zoomed region from crop editor. */
    focusDesktop?: ImageFocusPoint;
    focusMobile?: ImageFocusPoint;
  };
  theme?: 'light' | 'dark';             // Section theme (for text styling)
  /**
   * Default colour for Text components in this section (hex, CSS, or theme token name).
   * Per-block colour in the component editor overrides when set.
   */
  contentTextColor?: string;
  padding?: {
    top?: number;                 // Padding in px
    bottom?: number;
    left?: number;
    right?: number;
  };
  /**
   * When true, draws a frame (border + radius) around the entire section.
   * Theme with the same CSS variables as sub-section card borders: --page-subsection-border-color, --page-subsection-border-radius.
   */
  showBorder?: boolean;
  /**
   * Extra inset (px) added on all sides inside the border when showBorder is true.
   * Omitted = border only; use Content padding and Section padding for spacing.
   */
  borderPadding?: number;
  components?: SectionComponent[]; // Components within the section
  /**
   * Copy-link icon on the public section, yielding a direct link that opens the page
   * scrolled here. Sections holding a video are on automatically; set `false` to hide
   * the icon there, `true` to show it on a section without video.
   */
  showShareLink?: boolean;
  /**
   * Readable slug for that link, so it reads `/runway#runway-reel` instead of the raw
   * section id. Omitted → `#section-<id>`. See `utils/sectionShareLink`.
   */
  shareAnchor?: string;
  /**
   * When present, section was created or last stamped by the spec importer. Site renderers should ignore this field.
   */
  _importSource?: {
    template: string;
    specId?: string;
    specVersion?: number;
    importedAt?: string;
  };
  /**
   * Admin-only: page where this section was first created (informational).
   * Does not publish or keep the section in page lists — use `assignedPageIds` (pin) or page `sectionIds` (live).
   */
  anchorPageId?: string;
  /**
   * Admin-only: pages where this section is pinned in Section Settings lists.
   * Does not publish — use page `sectionIds` in Section Visibility for visitors.
   */
  assignedPageIds?: string[];
  /**
   * When set, this global section is owned by a product wizard (e.g. public casting apply).
   * Layout editor must not delete it; change the flow in the owning admin feature instead.
   */
  wizardManaged?: {
    kind: 'castingApply' | 'eventInquiryApply' | 'oneClickCheckout' | 'shopCatalog' | 'membersHub';
  };
  /** Optional datasource-driven tabs (content-pool enum). */
  sectionTabs?: SectionTabsConfig;
}

/** How a component block is displayed: direct on background, solid card (with or without inner border), or overlay with alpha. */
export type ComponentAppearance = 'transparent' | 'card' | 'cardNoBorder' | 'overlay';

/** Section-level block style preset: sets appearance + shadow in one pick. When set, overrides appearance / showShadow / elevation at render time. */
export type BlockStylePreset = 'minimal' | 'card' | 'floating' | 'bordered' | 'overlay';

/** Public casting application block; URL slug comes from /casting/:slug (not stored on config). */
export type CastingApplicationComponentConfig = Record<string, never>;

/** Members landing: tool links grid. Intended for Layout → Members page only. */
export interface MembersToolsHubComponentConfig {
  /** When true, hides the default “Members” title and subtitle (use a Text section above for a custom hero). */
  hidePageHeader?: boolean;
}

export interface SectionComponent {
  id: string;                    // Unique identifier
  /** Optional display name for admin (e.g. "Hero headline", "Calendar block"). Shown in Components and Sub-sections tabs. */
  name?: string;
  type: 'button' | 'imageCard' | 'imageWithText' | 'text' | 'divider' | 'badges' | 'services-grid' | 'publications-carousel' | 'image-carousel' | 'carousel' | 'subscriber-list' | 'magazine' | 'booking-wizard' | 'login' | 'phone-auth' | 'collection' | 'gallery' | 'media-grid' | 'shop' | 'single-product-checkout' | 'website-preview' | 'rsvp' | 'unsubscribe' | 'ambassador-portal' | 'event-coordinator' | 'event-inquiry-form' | 'event-details' | 'form' | 'site-message' | 'availability-calendar' | 'availability-cta-bar' | 'portfolio-collections' | 'rsvp-checkin' | 'testimonials' | 'faq' | 'logo-strip' | 'featured-item' | 'team' | 'designer-index-focus' | 'film-strip' | 'casting-application' | 'members-tools-hub' | 'model-measurements' | 'comp-card' | 'accordion-strip';  // Component type (film-strip kept for backward compat)
  order: number;                 // Order within section (for z-index/rendering order)
  /** For movable types only: 'custom' = use positionDesktop/positionMobile and allow drag in layout; 'default' or omitted = flow, no drag. Full-page types ignore this. */
  positioning?: 'default' | 'custom';
  positionDesktop?: {            // Desktop-specific positioning
    x: number;                   // X coordinate in pixels or percentage
    y: number;                   // Y coordinate in pixels or percentage
    unit?: 'px' | '%';           // Unit for coordinates (default: 'px')
  };
  positionMobile?: {             // Mobile-specific positioning
    x: number;                   // X coordinate in pixels or percentage
    y: number;                   // Y coordinate in pixels or percentage
    unit?: 'px' | '%';           // Unit for coordinates (default: 'px')
  };
  hideInMobile?: boolean;        // Hide component in mobile view
  /** When false, component is hidden on the page (admin visibility checkbox). Default true. */
  enabled?: boolean;
  /** Scroll entrance preset for this block. Omitted = type default or section stagger childPreset. */
  motion?: import('../motion/types').ComponentMotionPreset;
  /** Override stagger timeline index when section `layout.motion.mode` is `stagger`. */
  motionOrder?: number;
  /**
   * When this block is not listed in any `subSections[].componentIds`, places it in the full-width
   * stack above or below the sub-section column area. Omitted uses `layout.subSectionUnassignedPlacement`.
   */
  unassignedBandPlacement?: 'above' | 'below';
  /** When section tabs enabled: panel id matching `SectionTabEntry.value` or `SECTION_TAB_ALL_VALUE`. */
  tabKey?: string;
  /** Optional one-click block style; when set, overrides appearance / showShadow / elevation when rendering. */
  blockStylePreset?: BlockStylePreset;
  /** How the component block appears: transparent = on page/section background; card = solid card with border; cardNoBorder = solid fill without inner border; overlay = semi-transparent overlay. Default card. Ignored when blockStylePreset is set. */
  appearance?: ComponentAppearance;
  /** When appearance === 'overlay', opacity 0–1. Default 0.5. */
  overlayOpacity?: number;
  /** When appearance === 'overlay', background colour (hex e.g. #000000). Default #000000. */
  overlayColor?: string;
  /** When appearance is card-like (card or cardNoBorder), background colour (hex). Optional; default uses theme. */
  cardBackgroundColor?: string;
  /** When appearance is card-like (card or cardNoBorder), background image URL for the card/box. Optional. */
  cardBackgroundImage?: string;
  /** Floating effect: show drop shadow. When false, off. When true or omitted, elevation applies (omitted reads as on for older stored components). Ignored when blockStylePreset is set. */
  showShadow?: boolean;
  /** Shadow strength: none, low, medium, high. Used when showShadow is true. Ignored when blockStylePreset is set. */
  elevation?: 'none' | 'low' | 'medium' | 'high';
  /**
   * Optional outer frame around this component (any type). Independent of Appearance (card/overlay).
   * Configure under Components → Block frame (colour library, width, padding, radius).
   */
  frameBorderEnabled?: boolean;
  /** Frame border width in px when frame is enabled. Omitted = 1. */
  frameBorderWidthPx?: number;
  /** Frame border colour: theme token or hex (resolved like card/overlay colours). Omitted = theme border. */
  frameBorderColor?: string;
  /** Inner padding between frame border and content (px). Omitted = 24 (1.5rem). */
  framePaddingPx?: number;
  /** Frame corner radius (px). Omitted = 8. */
  frameBorderRadiusPx?: number;
  config: ButtonComponentConfig | ImageCardComponentConfig | ImageWithTextComponentConfig | TextComponentConfig | DividerComponentConfig | BadgesComponentConfig | ServicesGridComponentConfig | PublicationsCarouselComponentConfig | ImageCarouselComponentConfig | CarouselComponentConfig | SubscriberListComponentConfig | MagazineComponentConfig | BookingWizardComponentConfig | MemberAuthComponentConfig | CollectionComponentConfig | GalleryComponentConfig | MediaGridComponentConfig | ShopComponentConfig | SingleProductCheckoutComponentConfig | WebsitePreviewComponentConfig | RsvpComponentConfig | UnsubscribeComponentConfig | AmbassadorPortalComponentConfig | EventCoordinatorComponentConfig | EventInquiryFormComponentConfig | EventDetailsComponentConfig | FormComponentConfig | SiteMessageComponentConfig | AvailabilityCalendarComponentConfig | AvailabilityCtaBarComponentConfig | PortfolioCollectionsComponentConfig | RsvpCheckInComponentConfig | TestimonialsComponentConfig | FAQComponentConfig | LogoStripComponentConfig | FeaturedItemComponentConfig | TeamComponentConfig | DesignerIndexFocusComponentConfig | CastingApplicationComponentConfig | MembersToolsHubComponentConfig | ModelMeasurementsComponentConfig | CompCardComponentConfig | AccordionStripComponentConfig;
}

/** Check-in scanner section component. Staff only; requires userManagement feature. */
export interface RsvpCheckInComponentConfig {
  /** Optional heading above the scanner. */
  title?: string;
}

/** Unsubscribe section component (token-based from email links). */
export interface UnsubscribeComponentConfig {
  title?: string;
  confirmMessage?: string;
  successMessage?: string;
  redirectRoute?: string;
}

/** Ambassador Portal section component (login/dashboard block). */
export interface AmbassadorPortalComponentConfig {
  title?: string;
}

/** @deprecated Use gridType + productDisplay instead. Kept for backward compatibility. */
export type ShopDisplayStyle = 'card' | 'image-only' | 'circle' | 'pattern';

/**
 * Grid/layout type for the shop section: how items are arranged.
 * `default` is legacy and behaves like `default-horizontal`.
 */
export type ShopGridType =
  | 'default'
  | 'default-horizontal'
  | 'default-vertical'
  | 'circle'
  | 'pattern'
  | 'x-pattern'
  | 'corners';

/** How each product is displayed in the grid (image-only, full card, text only, or media/gif). */
export type ShopProductDisplay = 'image-only' | 'card' | 'text' | 'gif';

/** Visual scale for product thumbnails and card width hints in the shop grid. */
export type ShopTileSize = 's' | 'm' | 'l';

/** Shop section component: product grid. No dedicated /shop route required; paths from config. Requires shop feature. */
export interface ShopComponentConfig {
  /** Optional section title above the grid. */
  title?: string;
  /** Show "View all" link (default true). */
  showViewAll?: boolean;
  /** Where "View all" links to. If blank, uses layout page path for "shop" from config. */
  viewAllRoute?: string;
  /** Max number of products to show (optional; omit for all). */
  maxItems?: number;
  /** Grid/layout type: default horizontal/vertical flow, circle, pattern, x-pattern, corners. */
  gridType?: ShopGridType;
  /** How each product is shown: image-only, card, text, gif. */
  productDisplay?: ShopProductDisplay;
  /** Thumbnail / tile scale: S compact, M default, L prominent. */
  tileSize?: ShopTileSize;
  /** @deprecated Use gridType + productDisplay. If set, takes precedence for backward compat. */
  displayStyle?: ShopDisplayStyle;
}

/**
 * One-click checkout: curated shop products (tickets, etc.), optional CTA to open the picker,
 * add-to-cart from a list, then checkout. For matrix clothing (size/color), link to the product page instead.
 */
export interface SingleProductCheckoutComponentConfig {
  /** Curated Firestore product ids: `shops/{clientId}/products/{id}`. Order is the default display order. */
  productIds: string[];
  title?: string;
  /** Short line under the title (e.g. how to pick a ticket). */
  introText?: string;
}

/** Embeds another site in an iframe (`http` / `https` only). Many origins block framing; the UI falls back when load does not complete. */
export interface WebsitePreviewComponentConfig {
  /** Page URL to embed (stored as entered; renderer accepts only http/https). */
  url: string;
  /** Optional heading above the frame. */
  title?: string;
  /** Frame height in px (clamped when rendering). */
  minHeightPx?: number;
  /** Corner radius of the iframe shell. Omitted = CSS default (10px via --website-preview-shell-border-radius), independent of sub-section card radius. */
  shellCornerRadiusPreset?: SectionMediaCornerRadiusPreset;
}

/**
 * Optional mapping of content-pool item `data` keys → normalizer slots (image, name/title, description, category/role).
 * Matches `ContentPoolItemFieldMapping` in contentObjectNormalizer.
 */
export interface ContentPoolComponentFieldMapping {
  image?: string;
  name?: string;
  description?: string;
  category?: string;
  /** Designer index: pool key for list-card focus line. */
  focus?: string;
  /** Designer index: pool key for sticky-panel note. */
  note?: string;
  /** Designer index: pool key for comma-separated tags. */
  tags?: string;
  /** Designer index: pool key for image alt text. */
  imageAlt?: string;
}

/** Field mapping for designer-index-focus (extends pool slot overrides). */
export type DesignerIndexFocusFieldMapping = ContentPoolComponentFieldMapping;

/** When to show captions (item name/description). Shared by Gallery, Media grid, and Collection. */
export type GalleryCaptionVisibility = 'none' | 'hover' | 'always' | 'lightbox-only';

/** How collection items are ordered on the page (does not change Firestore). */
export type CollectionItemSort = 'manual' | 'createdDesc' | 'nameAsc';

/** Collection section component: shows media from a data source (portfolio or publications). */
export interface CollectionComponentConfig extends CardLinkCueConfig {
  /** Data source id from layout.dataSources. */
  dataSourceId?: string;
  /** Optional display title above the grid. */
  title?: string;
  /** When false, hides the title but keeps the View all action. Default true. */
  showTitle?: boolean;
  /** Optional: link for "View all" (path from config). If blank, uses portfolio path + section. */
  viewAllRoute?: string;
  /** Cap how many items render (teaser grids on the one-pager / section landing pages). Omit for all items. */
  maxItems?: number;
  /** Open full-screen lightbox on item click (images and video posters). Default true. */
  lightbox?: boolean;
  /** When to show captions under thumbnails. */
  captions?: GalleryCaptionVisibility;
  /** Grid columns at breakpoints (default 3 / 2 / 1). */
  columnsDesktop?: number;
  columnsTablet?: number;
  columnsMobile?: number;
  /** Gap between cells. */
  gap?: 'small' | 'medium' | 'large';
  /** Cell aspect ratio; `natural` lets media define height (no fixed ratio). */
  aspectRatio?: '1:1' | '3:4' | '4:3' | '16:9' | 'natural';
  /** How media fills the cell when aspect ratio is set. */
  crop?: 'cover' | 'contain';
  /** Video on the grid: poster opens lightbox vs inline player. */
  videoDisplay?: 'thumbnail-lightbox' | 'inline';
  /** Client-side sort for display order. `manual` matches Firestore `order`. */
  itemSort?: CollectionItemSort;
  /**
   * Portfolio-backed sources: show fashion week, show/category, and photo details on each tile.
   * Default true. Turn off for image-only grids.
   */
  showPortfolioMetadata?: boolean;
  /**
   * Portfolio runway: group by fashion week then show name. Defaults to `runway-by-show` when the
   * data source filters portfolio section `runway`.
   */
  portfolioGrouping?: 'flat' | 'runway-by-show';
  /** When true, filters items using the active subpage URL segment for this data source. */
  filterFromRoute?: boolean;
  /** Fixed first-segment filter for hub/teaser grids (uses the source's `subpages.segmentField`). */
  segmentFilter?: string;
  /** Fixed second-segment filter when the source defines `subpages.detailSegmentField`. */
  detailFilter?: string;
  /** Optional content-pool field overrides (portfolio sources ignore). */
  fieldMapping?: ContentPoolComponentFieldMapping;
  /** When true inside a tabbed section, uses filtered items from section tab context. Default false. */
  inheritTabFilter?: boolean;
}

/** Gallery layout mode: how images are arranged. */
export type GalleryLayoutMode =
  | 'grid'
  | 'masonry'
  | 'pattern'
  | 'featured-middle'
  | 'rectangle-hero';

/** Gallery pattern preset when layoutMode is 'pattern'. */
export type GalleryPatternPreset =
  | 'one-large-four-small'
  | 'bento-6'
  | 'alternating';

export type GalleryLayoutPreset =
  | 'diamond-carousel'
  | 'featured-grid'
  | 'horizontal-carousel'
  | 'horizontal-featured';

/** Gallery section component: data-driven gallery with layouts and lightbox. */
export interface GalleryComponentConfig extends CardLinkCueConfig {
  /** Data source id from layout.dataSources. Required. */
  dataSourceId: string;
  title?: string;
  showTitle?: boolean;
  viewAllRoute?: string;
  layoutMode: GalleryLayoutMode;
  patternPreset?: GalleryPatternPreset;
  /** Layout/style preset; maps to layout + item shape + nav. */
  layoutPreset?: GalleryLayoutPreset;
  heroSource?: 'first' | 'random' | 'featured';
  columnsDesktop?: number;
  columnsTablet?: number;
  columnsMobile?: number;
  gap?: 'small' | 'medium' | 'large';
  aspectRatio?: '1:1' | '4:3' | '16:9' | 'natural';
  crop?: 'cover' | 'contain';
  captions?: GalleryCaptionVisibility;
  lightbox?: boolean;
  lazyLoad?: boolean;
  videoDisplay?: 'thumbnail-lightbox' | 'inline';
  /** Optional content-pool field overrides. */
  fieldMapping?: ContentPoolComponentFieldMapping;
}

/** Media grid layout mode: simple (collection-style) or gallery layouts. */
export type MediaGridLayoutMode =
  | 'simple'
  | 'grid'
  | 'masonry'
  | 'pattern'
  | 'featured-middle'
  | 'rectangle-hero';

/** Media grid section component: unified component for Collection + Gallery. One dataSourceId, layout mode selects presentation. */
/**
 * Cards whose items carry a link (`externalUrl` / `internalRoute`, or a publication's `link`)
 * show a badge so visitors know they can open the full story, plus one line of copy above the
 * grid explaining it. See `utils/sectionTabCardLink` and `components/CardLinkCue`.
 */
export interface CardLinkCueConfig {
  /** Badge wording. Default: "Read more" for external links, "View" for internal routes. */
  cardLinkLabel?: string;
  /** Explainer above the grid. Defaults on when any card links out; `false` hides it. */
  showLinkHint?: boolean;
  /** Explainer copy override. */
  linkHint?: string;
}

export interface MediaGridComponentConfig extends CardLinkCueConfig {
  dataSourceId?: string;
  title?: string;
  showTitle?: boolean;
  viewAllRoute?: string;
  layoutMode: MediaGridLayoutMode;
  patternPreset?: GalleryPatternPreset;
  /** Gallery layout/style preset (diamond-carousel, featured-grid, horizontal-carousel, horizontal-featured). */
  layoutPreset?: GalleryLayoutPreset;
  heroSource?: 'first' | 'random' | 'featured';
  columnsDesktop?: number;
  columnsTablet?: number;
  columnsMobile?: number;
  gap?: 'small' | 'medium' | 'large';
  aspectRatio?: '1:1' | '4:3' | '16:9' | 'natural';
  crop?: 'cover' | 'contain';
  captions?: GalleryCaptionVisibility;
  lightbox?: boolean;
  lazyLoad?: boolean;
  videoDisplay?: 'thumbnail-lightbox' | 'inline';
  /** Optional content-pool field overrides. */
  fieldMapping?: ContentPoolComponentFieldMapping;
  /** When true inside a tabbed section, uses filtered items from section tab context. Default false. */
  inheritTabFilter?: boolean;
  /**
   * Multi-photo cards: page advance axis.
   * Vertical = magazine pages (side dots). Default vertical.
   */
  multiImageAxis?: 'horizontal' | 'vertical';
  /** Multi-photo cards: transition between pages. Default fade. */
  multiImageAnimation?: 'fade' | 'slide';
  /**
   * When true, omit the item shown by a sibling featured-item on the same tab
   * (avoids duplicating the Feature hero in the grid below).
   * Default: auto — on when a featured-item sibling shares this tab + inheritTabFilter.
   */
  excludeFeaturedItem?: boolean;
}

/**
 * Member sign-in section: email/password and phone OTP in one block (`SectionMemberAuthForm`).
 * Stored on both `login` and `phone-auth` section types; `defaultTab` picks the first tab when signed out.
 */
export interface MemberAuthComponentConfig {
  redirectRoute: string;
  defaultTab: 'email' | 'phone';
  /** Optional heading above the tab control */
  sectionTitle?: string;
  emailTitle: string;
  emailSubmitLabel: string;
  phoneTitle: string;
  phoneSubmitLabel: string;
  phoneVerifyLabel: string;
}

export type ButtonShapePreset =
  | 'rounded-rect'
  | 'blob'         // Blob rounded (CSS border-radius)
  | 'blob-stretch' // ENTER-style: irregular organic blob (SVG clip-path)
  | 'blob-liquid'  // @deprecated use blob-stretch (kept for backward compat)
  | 'pill'
  | 'arch'
  | 'badge'
  | 'organic-rect'
  | 'diamond'
  | 'hexagon'      // Elongated hex / arrow-like (BEGIN-style)
  | 'infinity';

/** 3D / premium look on top of shape and style. Flat = default; framed-3d = border + inner; glossy-3d = gradient; glow = soft halo. */
export type ButtonAppearancePreset = 'flat' | 'framed-3d' | 'glossy-3d' | 'glow';

/** Utility CTA: open a video in a lightbox (autoplay muted). */
export interface ButtonOpenVideoModalAction {
  type: 'openVideoModal';
  videoUrl: string;
  posterUrl?: string;
  title?: string;
}

/** One file in an openAsset / download CTA. */
export interface ButtonOpenAssetFile {
  url: string;
  filename?: string;
}

/** Utility CTA: open a file in a new tab and optionally trigger download. */
export interface ButtonOpenAssetAction {
  type: 'openAsset';
  /** First file (required for back-compat). */
  url: string;
  filename?: string;
  /** When true (default), attempt download with `filename` after opening. */
  download?: boolean;
  /**
   * Optional extra files (e.g. a second PDF). When present with download enabled,
   * files are merged into one PDF (fallback: sequential downloads).
   */
  additionalFiles?: ButtonOpenAssetFile[];
  /** Filename for the merged PDF when 2+ files are configured. */
  mergedFilename?: string;
}

export type ButtonAction = ButtonOpenVideoModalAction | ButtonOpenAssetAction;

export interface ButtonComponentConfig {
  label: string;
  icon?: string;                 // Optional: emoji or character shown before label (e.g. "→", "📧")
  buttonMappingId?: string;      // Reference to global button mapping
  route?: string;                 // Direct route (if not using mapping)
  /** When set, runs a utility action instead of navigating via route. */
  action?: ButtonAction;
  /** Built-in: primary | secondary | ghost | outline | text, or an id from `theme.customButtonVariants`. */
  style?: string;
  external?: boolean;
  target?: '_blank' | '_self';
  alignment?: 'left' | 'center' | 'right';
  /** Shape preset (pill, arch, diamond, etc.). */
  shapePreset?: ButtonShapePreset;
  /** 3D / premium appearance (framed, glossy, glow). Works with any shape. */
  appearancePreset?: ButtonAppearancePreset;
}

/** Maps to `.section-button-{style}` in section content. */
export const BUTTON_SECTION_STYLES = ['primary', 'secondary', 'ghost', 'outline', 'text'] as const;
export type ButtonSectionStyle = (typeof BUTTON_SECTION_STYLES)[number];

export const BUTTON_SECTION_STYLE_LABELS: Record<ButtonSectionStyle, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  ghost: 'Ghost',
  outline: 'Outline',
  text: 'Text',
};

/** Default shape + appearance for a section button style (Layout → Buttons). */
export type ThemeButtonStyleEntry = {
  shapePreset: ButtonShapePreset;
  appearancePreset: ButtonAppearancePreset;
};

export const DEFAULT_THEME_BUTTON_DEFAULTS: Record<ButtonSectionStyle, ThemeButtonStyleEntry> = {
  primary: { shapePreset: 'rounded-rect', appearancePreset: 'flat' },
  secondary: { shapePreset: 'rounded-rect', appearancePreset: 'flat' },
  ghost: { shapePreset: 'rounded-rect', appearancePreset: 'flat' },
  outline: { shapePreset: 'rounded-rect', appearancePreset: 'flat' },
  text: { shapePreset: 'pill', appearancePreset: 'flat' },
};

export type ThemeButtonDefaultsFirestore = Partial<
  Record<ButtonSectionStyle, Partial<ThemeButtonStyleEntry>>
>;

const KNOWN_BUTTON_SHAPE_PRESETS: readonly ButtonShapePreset[] = [
  'rounded-rect',
  'blob',
  'blob-stretch',
  'blob-liquid',
  'pill',
  'arch',
  'badge',
  'organic-rect',
  'diamond',
  'hexagon',
  'infinity',
];

const KNOWN_BUTTON_APPEARANCE_PRESETS: readonly ButtonAppearancePreset[] = [
  'flat',
  'framed-3d',
  'glossy-3d',
  'glow',
];

function coerceButtonShapePreset(raw: unknown, fallback: ButtonShapePreset): ButtonShapePreset {
  return typeof raw === 'string' && (KNOWN_BUTTON_SHAPE_PRESETS as readonly string[]).includes(raw)
    ? (raw as ButtonShapePreset)
    : fallback;
}

function coerceButtonAppearancePreset(raw: unknown, fallback: ButtonAppearancePreset): ButtonAppearancePreset {
  return typeof raw === 'string' && (KNOWN_BUTTON_APPEARANCE_PRESETS as readonly string[]).includes(raw)
    ? (raw as ButtonAppearancePreset)
    : fallback;
}

/** User-defined button variant (saved under `theme.customButtonVariants`). */
export type CustomButtonVariantDefinition = {
  id: string;
  name: string;
  /** Built-in colour skin for CSS `.section-button-{template}`. */
  template: ButtonSectionStyle;
  shapePreset: ButtonShapePreset;
  appearancePreset: ButtonAppearancePreset;
};

export const MAX_CUSTOM_BUTTON_VARIANTS = 24;

export function isBuiltinButtonSectionStyle(id: string): id is ButtonSectionStyle {
  return (BUTTON_SECTION_STYLES as readonly string[]).includes(id);
}

/** Lowercase slug; rejects empty, too long, or ids that collide with built-in style names. */
export function sanitizeCustomButtonVariantId(raw: string): string | null {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  if (s.length < 1 || s.length > 48) return null;
  if (isBuiltinButtonSectionStyle(s)) return null;
  return s;
}

function ensureUniqueCustomButtonVariantId(baseId: string, used: Set<string>): string {
  if (!used.has(baseId)) return baseId;
  let n = 2;
  while (used.has(`${baseId}-${n}`)) n += 1;
  return `${baseId}-${n}`;
}

function normalizeCustomButtonVariantCell(
  cell: unknown,
  index: number,
  usedIds: Set<string>
): CustomButtonVariantDefinition | null {
  if (!cell || typeof cell !== 'object' || Array.isArray(cell)) return null;
  const o = cell as Record<string, unknown>;
  const nameRaw = o.name;
  const name =
    typeof nameRaw === 'string' && nameRaw.trim() !== '' ? nameRaw.trim() : `Custom button ${index + 1}`;
  const fromId = typeof o.id === 'string' ? sanitizeCustomButtonVariantId(o.id) : null;
  const fromName = sanitizeCustomButtonVariantId(name);
  let baseId = fromId ?? fromName ?? `btn-variant-${index + 1}`;
  baseId = ensureUniqueCustomButtonVariantId(baseId, usedIds);
  usedIds.add(baseId);

  const tpl = o.template;
  const template: ButtonSectionStyle =
    typeof tpl === 'string' && (BUTTON_SECTION_STYLES as readonly string[]).includes(tpl)
      ? (tpl as ButtonSectionStyle)
      : 'primary';
  const baseDefaults = DEFAULT_THEME_BUTTON_DEFAULTS[template];
  return {
    id: baseId,
    name,
    template,
    shapePreset: coerceButtonShapePreset(o.shapePreset, baseDefaults.shapePreset),
    appearancePreset: coerceButtonAppearancePreset(o.appearancePreset, baseDefaults.appearancePreset),
  };
}

/** Normalizes `theme.customButtonVariants` from Firestore. */
export function extractCustomButtonVariantsFromTheme(raw: unknown): CustomButtonVariantDefinition[] {
  if (!Array.isArray(raw)) return [];
  const used = new Set<string>();
  const out: CustomButtonVariantDefinition[] = [];
  for (let i = 0; i < raw.length && out.length < MAX_CUSTOM_BUTTON_VARIANTS; i += 1) {
    const def = normalizeCustomButtonVariantCell(raw[i], i, used);
    if (def) out.push(def);
  }
  return out;
}

export function getCustomButtonVariantById(
  list: CustomButtonVariantDefinition[],
  id: string
): CustomButtonVariantDefinition | undefined {
  return list.find((v) => v.id === id);
}

/**
 * Maps any section-style id (built-in or custom variant) to a skin for generic `.button.button-{primary|secondary|ghost}`
 * (hero, services grid, etc.). Custom variants use their {@link CustomButtonVariantDefinition.template}.
 */
export function genericMappingButtonClassFromStyle(style: string | undefined): 'primary' | 'secondary' | 'ghost' {
  const cfg =
    typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null;
  const styleId = (style ?? 'primary').trim();
  const custom = getCustomButtonVariantById(
    extractCustomButtonVariantsFromTheme(cfg?.theme?.customButtonVariants),
    styleId
  );
  const skin: ButtonSectionStyle = custom
    ? custom.template
    : isBuiltinButtonSectionStyle(styleId)
      ? styleId
      : 'primary';
  if (skin === 'secondary') return 'secondary';
  if (skin === 'ghost') return 'ghost';
  return 'primary';
}

/** Normalizes `theme.buttonDefaults` from Firestore into full per-style entries. */
export function resolveButtonThemeDefaultsFromRaw(raw: unknown): Record<ButtonSectionStyle, ThemeButtonStyleEntry> {
  const out: Record<ButtonSectionStyle, ThemeButtonStyleEntry> = { ...DEFAULT_THEME_BUTTON_DEFAULTS };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const o = raw as Record<string, unknown>;
  for (const s of BUTTON_SECTION_STYLES) {
    const cell = o[s];
    if (!cell || typeof cell !== 'object' || Array.isArray(cell)) continue;
    const c = cell as Record<string, unknown>;
    out[s] = {
      shapePreset: coerceButtonShapePreset(c.shapePreset, out[s].shapePreset),
      appearancePreset: coerceButtonAppearancePreset(c.appearancePreset, out[s].appearancePreset),
    };
  }
  return out;
}

/**
 * Effective shape/appearance and colour skin for a section button: component overrides win; otherwise theme defaults
 * or custom variant defaults for `style`.
 */
export function effectiveButtonShapeAndAppearance(
  style: string | undefined,
  componentShape: ButtonShapePreset | undefined,
  componentAppearance: ButtonAppearancePreset | undefined
): {
  shapePreset: ButtonShapePreset;
  appearancePreset: ButtonAppearancePreset;
  /** Built-in skin for `.section-button-{colorSkin}` (from custom variant `template` when style is custom). */
  colorSkin: ButtonSectionStyle;
} {
  const cfg =
    typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null;
  const styleId = (style ?? 'primary').trim();
  const customList = extractCustomButtonVariantsFromTheme(cfg?.theme?.customButtonVariants);
  const custom = getCustomButtonVariantById(customList, styleId);
  if (custom) {
    return {
      shapePreset: componentShape ?? custom.shapePreset,
      appearancePreset: componentAppearance ?? custom.appearancePreset,
      colorSkin: custom.template,
    };
  }
  const styleKey: ButtonSectionStyle =
    styleId && (BUTTON_SECTION_STYLES as readonly string[]).includes(styleId) ? (styleId as ButtonSectionStyle) : 'primary';
  const d = resolveButtonThemeDefaultsFromRaw(cfg?.theme?.buttonDefaults);
  return {
    shapePreset: componentShape ?? d[styleKey].shapePreset,
    appearancePreset: componentAppearance ?? d[styleKey].appearancePreset,
    colorSkin: styleKey,
  };
}

export type ImageCardStylePreset =
  | 'rect-flat'
  | 'rect-recessed'
  | 'rect-angular-frame'
  | 'rect-thin-glow'
  | 'rect-physical-frame'
  | 'rect-asymmetric'
  | 'pill-glow'
  | 'diamond'
  | 'diamond-double';

/** Link target for parameterized data-source subpages. */
export interface ImageCardSubpageLink {
  dataSourceId: string;
  segment: string;
  detail?: string;
}

export interface ImageCardComponentConfig {
  /** Image URL or Storage path. Optional when using video/YouTube/embed. */
  imageUrl?: string;
  imageAlt?: string;             // Alt text
  /** Primary media: image (default), file, YouTube, or iframe embed. */
  mediaType?: ImageCardMediaType;
  /** Direct video file URL (mp4, webm, etc.) for mediaType videoFile. */
  videoUrl?: string;
  /** YouTube URL or 11-char video ID for mediaType youtube. */
  youtubeUrl?: string;
  /** Iframe src URL or pasted &lt;iframe&gt; HTML for mediaType embed. */
  embedSrcOrHtml?: string;
  /** Poster image URL for native &lt;video&gt;. */
  videoPosterUrl?: string;
  /** Native video controls (default true). */
  videoControls?: boolean;
  /** Autoplay hosted video or YouTube (starts muted; visitors can unmute). */
  videoAutoplay?: boolean;
  /** YouTube embed: rel=1 shows related from same channel (default false). */
  youtubeRel?: boolean;
  /** Name shown in the admin component list (so you can identify this card). Optional; falls back to title then "Unnamed". */
  name?: string;
  title?: string;                // Card title (shown on the page)
  description?: string;          // Card description
  link?: {
    route: string;
    external?: boolean;
    target?: '_blank' | '_self';
  };
  /**
   * Data-source subpage link. Resolves to `{hub}/{segment}` from Layout → Data sources.
   * Ignored when `link.route` is set.
   */
  subpageLink?: ImageCardSubpageLink;
  /**
   * @deprecated Use `subpageLink` with the runway data source. Kept for existing imports.
   * Portfolio fashion-week key (`nyfw`, `mfw`, …).
   */
  runwaySectionKey?: string;
  alignment?: 'left' | 'center' | 'right';
  width?: number | string;       // Width (px or percentage)
  height?: number | string;      // Height (px or percentage); with width/aspectRatio controls card size and image resize
  aspectRatio?: string;          // e.g., "16/9", "1/1", "4/3"
  /** How the image fills the card image area: cover = fill and crop, contain = fit inside, fill = stretch */
  imageObjectFit?: 'cover' | 'contain' | 'fill';
  /** Focal point for image crop. Optional cropArea stores zoomed region from crop editor. */
  imageFocus?: ImageFocusPoint;
  /** Show drop shadow on card. Default true. */
  showShadow?: boolean;
  /** Shadow elevation: none, low, medium, high. Ignored when showShadow is false. */
  elevation?: 'none' | 'low' | 'medium' | 'high';
  /** Card shape/frame preset; colours from theme. */
  stylePreset?: ImageCardStylePreset;
  /**
   * Optional corner radius for the media frame (clips image/video/embed). When set, overrides the radius from card style preset for the outer card shell.
   */
  mediaCornerRadiusPreset?: SectionMediaCornerRadiusPreset;
  /** Where to show title/description: below the image (default) or overlay on the image. */
  textLayout?: 'below' | 'overlay';
  /** Overlay only: position of the text block on the image (9-grid). */
  textOverlayPosition?: TextOverlayPosition;
  /** Overlay only: optional scrim for readability. */
  textOverlayScrim?: TextOverlayScrim;
  /** Overlay only: reuse Text component styling. */
  textOverlayStyle?: TextOverlayStyleOptions;
  /**
   * @deprecated Prefer `SectionComponent.frameBorderEnabled` and related fields (Components → Block frame).
   * When true, still honoured by the renderer as a legacy outer frame for image cards.
   */
  showBorder?: boolean;
  /** @deprecated Prefer `SectionComponent.framePaddingPx`. */
  borderPadding?: number;
  /** Click opens a full-screen lightbox with this card's image or hosted video (same asset). Takes precedence over link navigation. */
  lightboxOnClick?: boolean;
}

export type TextOverlayPosition =
  | 'top-left' | 'top-center' | 'top-right'
  | 'mid-left' | 'center' | 'mid-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right';

export type TextOverlayScrim = 'none' | 'bottom-bar' | 'full-dim' | 'top-bar';

export interface TextOverlayStyleOptions {
  variant?: string;
  textEffect?: TextEffectPreset;
  style?: {
    fontSize?: string;
    fontWeight?: string;
    color?: string;
  };
}

/** Placement for Image + Text “content blocks” (overlay on image vs footer strip below). */
export type ImageWithTextBlockPlacement = 'overlay' | 'footer';

/**
 * How blocks inside one content stack are arranged (same stack = same placement corner or footer segment).
 * Omitted defaults to a single vertical column.
 */
export type ImageWithTextStackBlocksLayout =
  | 'stack-vertical'
  | 'stack-horizontal'
  | 'grid-2'
  | 'grid-3'
  | 'grid-4';

/** Optional box styling around one content block (text / button / small image). */
export interface ImageWithTextBlockContainerStyle {
  backgroundColor?: string;
  /** Full CSS background (e.g. gradient); wins over backgroundColor when both set. */
  background?: string;
  padding?: string;
  borderRadius?: string;
  maxWidth?: string;
  border?: string;
}

/**
 * A block inside an Image + content stack (text, button, or small image). Placement and grid position live on the parent {@link ImageWithTextContentGroup}.
 */
export type ImageWithTextContentBlock =
  | {
      id: string;
      type: 'text';
      text: string;
      variant?: TextComponentConfig['variant'];
      alignment?: 'left' | 'center' | 'right';
      textEffect?: TextEffectPreset;
      style?: TextComponentConfig['style'];
      /** Styling for `[[a]]…[[/a]]`, `[[b]]…[[/b]]`, `[[c]]…[[/c]]` in this block (same as standalone Text component). */
      inlineMarks?: TextInlineMarksMap;
      containerStyle?: ImageWithTextBlockContainerStyle;
    }
  | {
      id: string;
      type: 'button';
      alignment?: 'left' | 'center' | 'right';
      label: string;
      icon?: string;
      route?: string;
      external?: boolean;
      target?: '_blank' | '_self';
      buttonStyle?: ButtonComponentConfig['style'];
      shapePreset?: ButtonComponentConfig['shapePreset'];
      appearancePreset?: ButtonComponentConfig['appearancePreset'];
      /** Optional label typography (applied on the button element). */
      labelStyle?: {
        fontSize?: string;
        fontWeight?: string;
      };
      containerStyle?: ImageWithTextBlockContainerStyle;
    }
  | {
      id: string;
      type: 'image';
      imageUrl: string;
      imageAlt?: string;
      width?: string;
      alignment?: 'left' | 'center' | 'right';
      containerStyle?: ImageWithTextBlockContainerStyle;
      /** Same media modes as Image card (optional; default image from `imageUrl`). */
      mediaType?: ImageCardMediaType;
      videoUrl?: string;
      youtubeUrl?: string;
      embedSrcOrHtml?: string;
      videoPosterUrl?: string;
      videoControls?: boolean;
      videoAutoplay?: boolean;
      youtubeRel?: boolean;
    }
  | {
      id: string;
      /** Renders a full section component by id from `PageSection.components` (same pool as sub-sections). */
      type: 'componentRef';
      componentId: string;
      containerStyle?: ImageWithTextBlockContainerStyle;
    };

/**
 * Ordered stack of content blocks with shared placement. Add multiple stacks for separate on-image corners,
 * mixed panel vs no-panel areas, or footer segments that flow naturally.
 */
export interface ImageWithTextContentGroup {
  id: string;
  placement: ImageWithTextBlockPlacement;
  /** When placement is overlay; defaults to bottom-center. */
  position?: TextOverlayPosition;
  blocks: ImageWithTextContentBlock[];
  /**
   * Layout of blocks inside this stack: vertical column (default), horizontal row with wrap,
   * or an equal-column CSS grid (2–4 columns).
   */
  blocksLayout?: ImageWithTextStackBlocksLayout;
  /** Tinted on-image panel for this stack only; omit or ≤0 for no panel. */
  clusterOverlayOpacity?: number;
  clusterOverlayColor?: string;
  /** Padding inside the tinted on-image panel for this stack. */
  clusterPaddingPreset?: IwtBlockPaddingPreset;
  /** Corner radius of the tinted on-image panel for this stack. */
  clusterBorderRadiusPreset?: SectionMediaCornerRadiusPreset;
  clusterPadding?: string;
  clusterBorderRadius?: string;
  /** Uniform gap between blocks in this stack (`gap`); default matches legacy 0.75rem when omitted. */
  blocksGapPreset?: IwtBlocksGapPreset;
  /**
   * Default padding inside each block’s rounded tile when `containerStyle.padding` is not set.
   * Use with grid layouts for even “measurement card” tiles.
   */
  blockPaddingPreset?: IwtBlockPaddingPreset;
  /**
   * Default corner radius for each block’s tile when `containerStyle.borderRadius` is not set.
   * When unset and the on-image tinted panel is active (`clusterOverlayOpacity` &gt; 0), the renderer uses the same radius as the panel.
   */
  blockCornerRadiusPreset?: SectionMediaCornerRadiusPreset;
}

/** Composite component: image with a text block positioned on top (same text options as Text component). */
export interface ImageWithTextComponentConfig {
  /** Name shown in admin component list. */
  name?: string;
  /** Hero still image URL (also optional poster for native video). */
  imageUrl?: string;
  imageAlt?: string;
  /** Hero media: same options as Image card (image / video file / YouTube / embed). */
  mediaType?: ImageCardMediaType;
  videoUrl?: string;
  youtubeUrl?: string;
  embedSrcOrHtml?: string;
  videoPosterUrl?: string;
  videoControls?: boolean;
  videoAutoplay?: boolean;
  youtubeRel?: boolean;
  aspectRatio?: string;
  imageObjectFit?: 'cover' | 'contain' | 'fill';
  /** Ordered stacks of blocks (on-image and/or footer). */
  contentGroups?: ImageWithTextContentGroup[];
  /** Footer row below the image (padding, background). */
  footerAlignment?: 'left' | 'center' | 'right';
  footerGap?: string;
  footerPadding?: string;
  /** Padding inside the footer row / text-only stack area (dropdown preset). */
  footerPaddingPreset?: IwtBlockPaddingPreset;
  /** Gap between footer stacks / blocks in the footer row. */
  footerGapPreset?: IwtBlocksGapPreset;
  footerBackground?: string;
  /**
   * When on-image stacks use **top-center**, **center**, or **bottom-center**, the layout spans the full image width;
   * this caps how wide each stack (panel or not) can grow. Corner anchors stay in a single column.
   */
  onImageClusterWidthPreset?: 'narrow' | 'medium' | 'wide' | 'xl' | 'full';
  /**
   * Corner radius for the outer Image + content card shell (inline on `.section-image-card`), including **text-only** blocks with no hero media.
   * When set, overrides the shell radius from card appearance / theme. Also clips hero image/video/embed when present.
   */
  mediaCornerRadiusPreset?: SectionMediaCornerRadiusPreset;
  /** Card shape/frame preset; colours from theme. */
  stylePreset?: ImageCardStylePreset;
  /** Horizontal placement of the whole card in the section (left/center/right). */
  cardAlignment?: 'left' | 'center' | 'right';
  /**
   * Vertical placement when the column is taller than the card (e.g. full-height hero).
   * Omitted or `top`: natural flow at the top. `center` / `bottom`: pin the whole card in extra space.
   */
  cardAlignmentVertical?: 'top' | 'center' | 'bottom';
  width?: number | string;
  height?: number | string;
  link?: {
    route: string;
    external?: boolean;
    target?: '_blank' | '_self';
  };
  /**
   * @deprecated Prefer `SectionComponent.frameBorderEnabled` and related fields (Components → Block frame).
   * When true, still honoured by the renderer as a legacy outer frame.
   */
  showBorder?: boolean;
  /** @deprecated Prefer `SectionComponent.framePaddingPx`. */
  borderPadding?: number;
}

/** Text effect presets for headlines and display text (e.g. metallic shine, glow). */
export type TextEffectPreset =
  | 'none'
  | 'shine-gold'    // Gold metallic shine with highlight
  | 'shine-silver'  // Silver metallic shine
  | 'metallic-gold' // Rich gold, less highlight
  | 'glow-gold'     // Soft gold glow
  | 'glow-soft';    // Subtle glow using current color

export interface TextComponentConfig {
  text: string;                  // Text content (headline, subheadline, etc.)
  /** Built-in: headline | subheadline | body | caption | label, or a custom id from theme.typography.customVariants. */
  variant?: string;
  alignment?: 'left' | 'center' | 'right';
  /** Visual effect preset (shine, metallic, glow). Best with headline/subheadline. */
  textEffect?: TextEffectPreset;
  style?: {
    fontSize?: string;           // e.g., "2rem", "1.5rem"
    fontWeight?: string;         // e.g., "bold", "600", "normal"
    color?: string;              // Text color override (ignored when textEffect uses gradient)
  };
  /** Styling for marked phrases: `[[a]]…[[/a]]`, `[[b]]…[[/b]]`, `[[c]]…[[/c]]` in this block only. */
  inlineMarks?: TextInlineMarksMap;
}

/** Divider line between content blocks. Customisable colour, thickness, width, style. */
export interface DividerComponentConfig {
  /** Line colour (hex or CSS). Default rgba(0,0,0,0.15). */
  color?: string;
  /** Thickness in px. Default 1. */
  thickness?: number;
  /** Width: 'full' = 100%, or number 1–100 = percentage of container. Default full. */
  width?: 'full' | number;
  /** Border style. Default solid. */
  style?: 'solid' | 'dashed' | 'dotted' | 'double';
}

export type BadgeShapePreset =
  | 'crest'
  | 'circle-rating'
  | 'octagon'
  | 'circle-sunburst'
  | 'pill-ornate'
  | 'notched-rect'
  | 'frame-image'
  | 'ornate-rect'
  | 'hexagon';

export interface BadgesComponentConfig {
  badges: string[];              // Array of badge/chip labels
  alignment?: 'left' | 'center' | 'right';
  style?: 'default' | 'outlined' | 'filled';
  /** Shape/style preset; colours from theme. */
  shapePreset?: BadgeShapePreset;
}

export interface ServicesGridComponentConfig {
  title?: string;                // Optional section title (default: "SERVICES")
  showTitle?: boolean;           // Whether to show title (default: true)
  gridColumns?: number;          // Number of columns (default: auto-fit)
  gap?: number;                 // Gap between cards in px (default: 30)
  maxWidth?: number;            // Max width of grid container in px (default: 1200)
  /** When not false, show a Book button per service that opens the inline booking wizard (requires bookings feature). */
  showBookButton?: boolean;
  /** Overrides for the inline booking wizard (title, success copy, etc.). */
  bookingWizard?: Partial<BookingWizardComponentConfig>;
  // Services are loaded from Firestore via useServices hook
  // No need to store service IDs - component loads all active services
}

export interface PublicationsCarouselComponentConfig {
  /** Optional title above the carousel. Leave empty to use as a generic carousel with no heading. */
  title?: string;
  showTitle?: boolean;           // Whether to show section title when set (default: true)
  /** When true, hide all text on each card (title, issue, description) — images only. */
  hideCardText?: boolean;
  carouselSpeed?: number;        // Animation speed in seconds (default: 40)
  cardWidth?: number;           // Width of each card in px (default: 375)
  // Publications are loaded from Firestore via publications collection
  // No need to store publication IDs - component loads all active publications
}

/** Each slide is either a still image or a video clip — not both as the main asset. */
export type ImageCarouselMediaKind = 'image' | 'video';

/** Single slide in the image carousel. */
export interface ImageCarouselSlide {
  id: string;
  /** Whether this slide is a still image or a short video. Omit on older saves: treated as video if `videoUrl` is set, else image. */
  mediaKind?: ImageCarouselMediaKind;
  /** Still image URL when `mediaKind` is `image`. When `mediaKind` is `video`, optional poster frame only. */
  imageUrl?: string;
  imageAlt?: string;
  /** Short clip URL when `mediaKind` is `video` (mp4/webm). Muted, plays inline, loops. */
  videoUrl?: string;
  /** Optional URL: makes the image (and on-image text) the click target. Relative paths (e.g. /shop) stay in-tab; http(s) opens in a new tab. */
  linkUrl?: string;
  /** Short label drawn over the bottom of the image (plain text; line breaks preserved). */
  overlayText?: string;
  /** Optional caption below the image (plain text; line breaks preserved). */
  captionBelow?: string;
  /** Optional headline when carousel card style shows a title (preferred over alt-only display). */
  slideTitle?: string;
  /** Optional body when carousel card style shows a description. */
  slideDescription?: string;
  /** When true, treat as no-background (transparent PNG); style with floating shadow. */
  removeBackground?: boolean;
  /** Per-slide width in px (optional; fallback to global slideWidth). */
  width?: number;
  /** Per-slide height in px (optional; fallback to global slideHeight). */
  height?: number;
}

/** Resolve slide media kind for render/admin (supports slides saved before `mediaKind` existed). */
export function effectiveImageCarouselMediaKind(
  slide: Pick<ImageCarouselSlide, 'mediaKind' | 'videoUrl'>
): ImageCarouselMediaKind {
  if (slide.mediaKind === 'image' || slide.mediaKind === 'video') return slide.mediaKind;
  return slide.videoUrl?.trim() ? 'video' : 'image';
}

/** Carousel card style: how much content to show on each slide. */
export type CarouselCardStyle = 'image-only' | 'image+title' | 'image+title+description' | 'full';

/** Carousel scroll direction. */
export type CarouselScrollDirection = 'ltr' | 'rtl';

export type CarouselLayoutPreset =
  | 'overlap-center'
  | 'fullwidth-slide'
  | 'blur-circles'
  | 'cards-row'
  | 'layered-card'
  | 'hero-content'
  | 'immersive-circular-nav'
  | 'thumbnail-grid';

/** Unified carousel: data from dataSourceId or inline slides; scroll direction and card style. */
export interface CarouselComponentConfig {
  /** When set, use items from this data source (portfolio or content-pool). When empty, use slides. */
  dataSourceId?: string;
  /** Inline slides when dataSourceId is not set (image carousel style). */
  slides?: ImageCarouselSlide[];
  title?: string;
  showTitle?: boolean;
  cardStyle?: CarouselCardStyle;
  scrollDirection?: CarouselScrollDirection;
  carouselSpeed?: number;
  cardWidth?: number;
  slideWidth?: number;
  slideHeight?: number;
  showEntireImage?: boolean;
  showShadow?: boolean;
  elevation?: 'none' | 'low' | 'medium' | 'high';
  /** Layout/style preset; layout, card shape, nav. */
  layoutPreset?: CarouselLayoutPreset;
  /** Optional content-pool field overrides when using dataSourceId. */
  fieldMapping?: ContentPoolComponentFieldMapping;
  /** When true inside a tabbed section, uses filtered items from section tab context. */
  inheritTabFilter?: boolean;
}

export interface ImageCarouselComponentConfig {
  title?: string;
  showTitle?: boolean;
  /** Full loop duration in seconds. Default 40. */
  carouselDuration?: number;
  /** Default width of each slide in px. Default 320. */
  slideWidth?: number;
  /** Default height of each slide in px. Optional. */
  slideHeight?: number;
  /** Show entire image inside each slide (object-fit: contain). Default true. */
  showEntireImage?: boolean;
  showShadow?: boolean;
  elevation?: 'none' | 'low' | 'medium' | 'high';
  slides: ImageCarouselSlide[];
}

export interface MagazineComponentConfig {
  /** Cover image URL (section config). Page 0 is this cover; inner pages are publications. */
  coverImageUrl?: string;
  showPageNumbers?: boolean;     // Show "p. 2" or "2 / N" (default: true)
  cornerTapZone?: 'right' | 'bottom-right' | 'both';  // Where to tap to turn (default: 'right')
  theme?: 'light' | 'dark';      // Page number and UI (default: 'light')
  /** Auto-turn pages every N seconds; 0 or omit = no auto-turn */
  autoTurnIntervalSeconds?: number;
  // Publications loaded from Firestore (same as Publications Carousel)
}

export interface BookingWizardComponentConfig {
  title?: string;                // Optional section title above wizard
  submitButtonLabel?: string;    // e.g. "Request booking" (default: "Request booking")
  animation?: 'slide' | 'fade' | 'none';
  showProgress?: boolean;        // Stepper or progress bar (default: true)
  successMessage?: string;        // Shown after submit (optional)
  redirectAfterSuccess?: string; // Route to redirect to after success (optional, empty = stay)
}

/** Single field in a form row (text, email, tel, textarea, number, select). */
export interface FormFieldConfig {
  type: 'text' | 'email' | 'tel' | 'textarea' | 'number' | 'select';
  label: string;
  name: string;
  placeholder?: string;
  required?: boolean;
  /** Option labels when type === 'select'; submitted value matches the chosen label string. */
  options?: string[];
  /**
   * When type === 'select', load options from Firestore instead of static `options`.
   * `services` — active services (and tiers) from Services Management.
   */
  optionsSource?: 'services';
}

/** A row contains 1 or 2 fields (rendered side-by-side when 2). */
export interface FormRowConfig {
  fields: FormFieldConfig[];
}

/** Event Details section component: location(s), arrival time, agenda, photo, dress code, parking — everything guests need to get to the event. */
export interface EventDetailsLocationConfig {
  name?: string;
  address: string;
  mapLink?: string;
}

export interface EventDetailsAgendaItemConfig {
  time: string;
  description: string;
}

export interface EventDetailsComponentConfig {
  title?: string;
  /** One or more venues (e.g. ceremony + reception). */
  locations?: EventDetailsLocationConfig[];
  /** e.g. "6:00 PM" or "Doors open 5:30 PM" */
  timeOfArrival?: string;
  /** Agenda with time and description. */
  agendaItems?: EventDetailsAgendaItemConfig[];
  /** Photo of the location (URL). */
  locationPhotoUrl?: string;
  /** e.g. "Black tie" or "Smart casual / Cocktail attire" */
  dressCode?: string;
  /** Parking information. */
  parking?: string;
  /** Layout: vertical (stacked) or horizontal (location, time, agenda in a row). Default vertical. */
  layout?: 'vertical' | 'horizontal';
  /** When true, show a color overlay on the location photo (e.g. for text contrast). */
  overlayEnabled?: boolean;
  /** Overlay color (hex). Used when overlayEnabled is true. Default #000000. */
  overlayColor?: string;
  /** Overlay opacity 0–1. Used when overlayEnabled is true. Default 0.5. */
  overlayOpacity?: number;
}

/** Testimonials section: items from data source (quote, author, optional image). */
export interface TestimonialsComponentConfig {
  dataSourceId: string;
  title?: string;
  /** Layout: grid of cards or carousel. Default grid. */
  layout?: 'grid' | 'carousel';
  fieldMapping?: ContentPoolComponentFieldMapping;
}

/** FAQ section: items from data source (question, answer). Accordion UI. */
export interface FAQComponentConfig {
  dataSourceId: string;
  title?: string;
  fieldMapping?: ContentPoolComponentFieldMapping;
}

/** Logo strip: horizontal row of logos/images from data source. */
export interface LogoStripComponentConfig {
  dataSourceId: string;
  /**
   * Section heading. In `featured` layout this is unused when eyebrowLabel is set;
   * otherwise it can still show as a standard title in row/grid/mosaic.
   * In `hero-collage`, this is the elegant page identity inside the card.
   */
  title?: string;
  /** Optional supporting line under the hero-collage title. */
  subtitle?: string;
  /**
   * Featured-in eyebrow (e.g. "FEATURED IN"). Shown above the strip in `featured` layout.
   * A short rule/em dash is rendered after the label.
   */
  eyebrowLabel?: string;
  /** Placement mode for logos. `hero-collage` = full-bleed artsy logo field (above tabs). */
  layoutMode?: 'row' | 'grid' | 'mosaic' | 'featured' | 'hero-collage';
  /** Pattern style used when layoutMode is mosaic. */
  mosaicPattern?: 'checkerboard' | 'diagonal' | 'featured-center';
  /** Image sizing strategy. */
  logoSizeMode?: 'uniform' | 'auto';
  /** Preset size when logoSizeMode is uniform. */
  logoSize?: 'sm' | 'md' | 'lg';
  /** Minimum logo cell width in px when logoSizeMode is auto. */
  logoMinWidth?: number;
  /** Maximum logo cell width in px when logoSizeMode is auto. */
  logoMaxWidth?: number;
  /** How logo images fill their cells. */
  logoAspectMode?: 'contain' | 'cover';
  /** Spacing density between logos. */
  gap?: 'tight' | 'normal' | 'airy';
  /** Preferred columns in grid/mosaic mode. */
  columns?: number;
  /** Vertical rules between logos (default on for featured). */
  showDividers?: boolean;
  /**
   * Max logos to show before “+ N MORE”. Omit or 0 = show all.
   * Featured mock typically uses 4.
   */
  maxVisible?: number;
  /** Override overflow copy; use `{n}` for remaining count. Default: `+ {n} MORE`. */
  moreLabel?: string;
  /** CTA under the strip (e.g. VIEW ALL PUBLICATIONS →). */
  viewAllLabel?: string;
  /** Internal route for the view-all CTA (e.g. `/press`). */
  viewAllRoute?: string;
  /**
   * Logo color treatment.
   * - `adapt` (default): black on light section surfaces, white on dark — follows section theme /
   *   colour-library background (same idea as featured-publications logo bar).
   * - Forced filters override adaptation when you need a fixed look.
   */
  logoFilter?: 'adapt' | 'none' | 'grayscale' | 'monochrome-light' | 'monochrome-dark';
  fieldMapping?: ContentPoolComponentFieldMapping;
  /** When true inside a tabbed section, uses filtered items from section tab context. */
  inheritTabFilter?: boolean;
  /**
   * When true, show at most one logo per identity key (see uniqueByField).
   * Use when the same publications data source has multiple features for one magazine.
   */
  uniqueByName?: boolean;
  /**
   * Content-pool field used for uniqueness when uniqueByName is true (e.g. `publication` enum).
   * Empty / omitted → fall back to display title/name.
   */
  uniqueByField?: string;
}

export type FeaturedItemLayoutPreset =
  | 'image-left'
  | 'image-right-diamond'
  | 'image-left-carousel'
  | 'image-right-multi';

/** Designer directory with selectable list + detail focus panel (fashion house / runway). */
export interface DesignerIndexFocusDesignerConfig {
  id: string;
  name: string;
  role: string;
  focus: string;
  tags: string[];
  note: string;
  imageSrc: string;
  imageAlt: string;
}

export interface DesignerIndexFocusComponentConfig {
  /** Content pool with one row per designer (Layout → Data sources). */
  dataSourceId: string;
  fieldMapping?: DesignerIndexFocusFieldMapping;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  designerVisualGuidance?: string;
  focusPanel: {
    eyebrow: string;
    note: string;
  };
}

/** Featured item: single highlighted item from data source (first item) or inline image/title/description/link. */
export interface FeaturedItemComponentConfig {
  /** When set, use first item from this data source. When empty, use inline fields. */
  dataSourceId?: string;
  title?: string;
  description?: string;
  imageUrl?: string;
  link?: string;
  linkLabel?: string;
  /** Layout preset; image position, frame shape, CTA style. */
  layoutPreset?: FeaturedItemLayoutPreset;
  /** When dataSourceId is set: optional content-pool field overrides (image, title, body). */
  fieldMapping?: ContentPoolComponentFieldMapping;
  /** When true inside a tabbed section, uses filtered items from section tab context. */
  inheritTabFilter?: boolean;
}

/** Team card layout preset (public site + preview). */
export type TeamLayoutPreset = 'grid' | 'list' | 'cards' | 'compact';

/** Photo frame shape for team member portraits. */
export type TeamPhotoShapePreset = 'circle' | 'rounded' | 'square' | 'arch';

/** Photo frame edge length (square crop area before shape masking). */
export type TeamPhotoSizePreset = 'sm' | 'md' | 'lg' | 'xl';

/** Portrait / crop aspect for the photo frame (width / height). Default 1/1 when omitted. */
export type TeamPhotoAspectPreset = '1/1' | '4/5' | '3/4';

/**
 * How the member photo fills the frame.
 * - `contain` — show the entire image (frame height follows the photo; no top/bottom crop). Product default.
 * - `cover` — fill the aspect frame and crop overflow (classic avatar crop).
 */
export type TeamPhotoFitPreset = 'contain' | 'cover';

/** Which rows from the data source the Team block shows (content-pool boolean filter is optional). */
export type TeamMemberVisibility = 'all' | 'flaggedInPool';

/** Team section: profile cards from data source (name, role, image, bio). */
export interface TeamComponentConfig {
  dataSourceId: string;
  title?: string;
  layout?: TeamLayoutPreset;
  /** Crop frame shape; product default `rounded` when omitted. */
  photoShape?: TeamPhotoShapePreset;
  /** Square crop edge length; product default `md` when omitted. */
  photoSize?: TeamPhotoSizePreset;
  /** Photo area aspect ratio; default 1/1 (square). Use 4/5 for editorial portrait columns. */
  photoAspectRatio?: TeamPhotoAspectPreset;
  /** Image fit inside the frame; product default `contain` (full photo visible). */
  photoFit?: TeamPhotoFitPreset;
  /** Pool keys: `category` → role line, `description` → bio. */
  fieldMapping?: ContentPoolComponentFieldMapping;
  /**
   * `all` — every active item from the source.
   * `flaggedInPool` — content pool only: only items whose `data[memberFlagPoolKey]` is `true` (boolean).
   */
  memberVisibility?: TeamMemberVisibility;
  /**
   * Content-pool item `data` key for a boolean field. Required when `memberVisibility` is `flaggedInPool`
   * (e.g. `preview`, `featureOnHome`).
   */
  memberFlagPoolKey?: string;
}

export type ModelMeasurementsDataSource = 'page-model' | 'model' | 'portfolio-meta' | 'static';

export type ModelMeasurementsFieldPreset = 'runway' | 'roster';

export type ModelMeasurementsLayoutPreset = 'card-grid' | 'spec-sheet';

/** Layout block: measurement card grid (runway comp-card or roster model doc). */
export interface ModelMeasurementsComponentConfig {
  title?: string;
  subtitle?: string;
  fieldPreset?: ModelMeasurementsFieldPreset;
  /** Where values are loaded from on the live site. */
  dataSource?: ModelMeasurementsDataSource;
  /** When `dataSource` is `model`, load `models/{clientId}/models/{modelId}.measurements`. */
  modelId?: string;
  /** When `dataSource` is `static`, inline values keyed by field preset keys. */
  staticValues?: Record<string, string>;
  /** When `dataSource` is `static`, optional display label overrides keyed by field key. */
  staticLabels?: Record<string, string>;
  showActions?: boolean;
  hideEmpty?: boolean;
  headerLayout?: 'default' | 'centered';
  /** `card-grid` = centered tiles (default). `spec-sheet` = portfolio spec table with underlined labels. */
  layoutPreset?: ModelMeasurementsLayoutPreset;
  /**
   * When true and `dataSource` is a real profile (not static), render the full Comp Card
   * (name / email / IG / 4 photos + measurements) for that same profile.
   */
  showCompCard?: boolean;
  /** Photo plate layout when `showCompCard` is enabled. Default interlock. */
  compCardPhotoLayout?: 'interlock' | 'equal';
  /** Show PNG/PDF download on the embedded Comp Card. Default true. */
  compCardShowDownload?: boolean;
}

/**
 * Where a Comp Card block loads its measurement profile (+ package) from.
 * Same binding options as model-measurements (minus static — a card needs a real profile).
 */
export type CompCardProfileSource = 'page-model' | 'model' | 'portfolio-meta';

/**
 * Layout block: full printable/shareable comp card.
 * Binds to a measurement profile; package extras live on that profile's `compCard` field.
 * Optional identity overrides are for one-off/preview only — prefer editing the profile package.
 */
export interface CompCardComponentConfig {
  /** Measurement + package profile binding. Default `portfolio-meta`. */
  profileSource?: CompCardProfileSource;
  fieldPreset?: ModelMeasurementsFieldPreset;
  /** When `profileSource` is `model`. */
  modelId?: string;
  showDownload?: boolean;
  /** Photo plate layout. Default `interlock` (fashion offset). */
  photoLayout?: 'interlock' | 'equal';
  /** Optional display overrides (do not replace profile package as source of truth). */
  displayName?: string;
  email?: string;
  instagramHandle?: string;
}

export type AccordionStripItemKind = 'text' | 'measurements';

export interface AccordionStripMeasurementsConfig {
  fieldPreset?: ModelMeasurementsFieldPreset;
  layoutPreset?: ModelMeasurementsLayoutPreset;
  dataSource?: ModelMeasurementsDataSource;
  modelId?: string;
  staticValues?: Record<string, string>;
  staticLabels?: Record<string, string>;
  showActions?: boolean;
  hideEmpty?: boolean;
}

export interface AccordionStripItemConfig {
  label: string;
  kind?: AccordionStripItemKind;
  body?: string;
  measurements?: AccordionStripMeasurementsConfig;
}

/** Portfolio footer accordion rows (measurements, availability, etc.). */
export interface AccordionStripComponentConfig {
  items: AccordionStripItemConfig[];
  defaultOpenIndex?: number | null;
}

export type FormStylePreset =
  | 'minimal-glow'
  | 'ornate-frame'
  | 'glass-panel'
  | 'classic-bordered';

/** Opt-in for email/SMS copy or updates when submitting a form. Hooks into platform SMS/email. */
export interface FormNotifyOptInConfig {
  /** Show "Send me a copy by email" checkbox. */
  emailCopy?: boolean;
  /** Show "Receive updates by SMS" checkbox. */
  smsUpdates?: boolean;
  /** Custom label for email opt-in. */
  emailLabel?: string;
  /** Custom label for SMS opt-in. */
  smsLabel?: string;
}

/** Form section component: configurable rows of input fields for custom forms (contact, etc.). */
export interface FormComponentConfig {
  /** Heading shown above the form on the public page (optional). */
  title?: string;
  /**
   * Label for grouping in Members → Form submissions when the visitor did not pick a service.
   * Service inquiry forms group by the chosen service instead.
   */
  submissionTitle?: string;
  /**
   * When true, visitors pick an active service from Services Management (same catalog as the booking wizard).
   * Submissions are grouped by service name in Members → Form submissions.
   */
  serviceInquiry?: boolean;
  submitLabel?: string;
  successMessage?: string;
  rows: FormRowConfig[];
  /** Panel/input style preset; colours from theme. */
  stylePreset?: FormStylePreset;
  /** Optional opt-in for email copy and/or SMS updates on submit. Hooks into platform SMS/email when backend is wired. */
  notifyOptIn?: FormNotifyOptInConfig;
  /** Cap form width in pixels. Omit so the block uses the full content column width. */
  maxWidthPx?: number;
}

/**
 * Simple outbound message block: visitor picks email or SMS, enters subject + body.
 * Delivery goes to `contact.email` (email) or `contact.phone` (SMS) from client config — set in site settings.
 */
export interface SiteMessageComponentConfig {
  title?: string;
  introText?: string;
  submitLabel?: string;
  successMessage?: string;
  /** Panel + inputs + toolbar + submit match the Form component presets (theme tokens). */
  stylePreset?: FormStylePreset;
  /** Show Markdown formatting toolbar. Default true. */
  showFormattingToolbar?: boolean;
  /** Show live preview under the message field. Default true. */
  showPreview?: boolean;
}

/** Horizontal contact bar: status, email, social, and inline booking or availability calendar panel. */
export interface AvailabilityCtaBarComponentConfig {
  statusLabel: string;
  emailLabel: string;
  emailHref: string;
  socialLabel: string;
  socialHref: string;
  /** Right-side action: opens inline booking wizard or availability calendar. */
  primaryAction: 'book' | 'request-availability';
  bookLabel?: string;
  requestAvailabilityLabel?: string;
  variant?: 'light' | 'dark';
  /** Fallback route when bookings feature is off and primary action is book. */
  bookRoute?: string;
  bookingWizard?: Partial<BookingWizardComponentConfig>;
  availabilityCalendar?: Partial<AvailabilityCalendarComponentConfig>;
}

/** Public availability month grid + date check (reads Firestore availability collections). */
export interface AvailabilityCalendarComponentConfig {
  title?: string;
  introText?: string;
  availableLabel?: string;
  unavailableLabel?: string;
  checkDateLabel?: string;
  /** Shown when no availability periods exist (instead of the calendar). */
  noAvailabilityMessage?: string;
  /** Extra line inviting visitors to contact or submit a request elsewhere on the site. */
  noAvailabilityCallout?: string;
  /** Optional layout page id whose Form component is embedded when no availability (default: contact page at /contact). */
  contactFormPageId?: string;
}

/** Portfolio collections section: grid of data source cards linking to each portfolio area. */
export interface PortfolioCollectionsComponentConfig {
  title?: string;
}

/**
 */
export interface ButtonMapping {
  id: string;                    // Unique identifier (e.g., "hero-cta-primary")
  label: string;                 // Button text
  route: string;                 // Internal route or external URL (supports {variables})
  style?: string;
  icon?: string;                 // Optional icon (emoji or icon name)
  target?: '_blank' | '_self';   // Link target
  external?: boolean;            // Auto-detect if route starts with http
  requiresAuth?: boolean;        // Require authentication
  requiresAdmin?: boolean;       // Require admin access
  requiresFeature?: string;      // Require feature enabled
  analytics?: {
    eventName?: string;          // Analytics event name
    category?: string;            // Event category
  };
}

export type ComponentMappingType = 'button' | 'product' | 'card';

export interface ComponentMapping {
  id: string;                    // Unique identifier
  type: ComponentMappingType;    // Component type: button, product, or card
  label: string;                 // Display text/title
  
  // Common fields
  route?: string;                // Internal route or external URL (supports {variables})
  external?: boolean;            // Auto-detect if route starts with http
  target?: '_blank' | '_self';   // Link target
  style?: string;
  icon?: string;                 // Optional icon (emoji or icon name)
  
  // Visibility conditions
  requiresAuth?: boolean;
  requiresAdmin?: boolean;
  requiresFeature?: string;
  
  // Analytics
  analytics?: {
    eventName?: string;
    category?: string;
  };
  
  // Type-specific fields
  product?: {
    productId: string;           // Reference to product in shop
    imageUrl?: string;           // Override product image (optional)
    showPrice?: boolean;         // Show/hide price (default: true)
    showDescription?: boolean;   // Show/hide description (default: true)
  };
  
  card?: {
    imageUrl: string;            // Card image (required)
    title: string;               // Card title
    description?: string;        // Card description
    imageAspectRatio?: string;   // e.g., "16/9", "1/1", "4/3"
  };
}

export interface HomepageSection {
  id: 'publications' | 'services';
  enabled: boolean;
  order: number; // Display order (0, 1, 2, ...)
}

/** Legacy nav link id (no longer injected). Filtered from stored `navigation.links` so old rows do not duplicate manual links. */
export const EVENT_INQUIRY_NAV_LINK_ID = 'event-inquiry-press-volunteers';

export interface NavLink {
  id: string;                    // Unique identifier (e.g., "home", "portfolio")
  label: string;                 // Display text (e.g., "Home", "Portfolio")
  route: string;                 // Internal route (e.g., "/", "/portfolio") or external URL
  order?: number;                // Display order (default: append to end)
  visible?: boolean;             // Visibility flag (default: true)
  requiresAuth?: boolean;        // Require authentication (default: false)
  requiresAdmin?: boolean;       // Require admin access (default: false)
  requiresMembersArea?: boolean; // Require canAccessMembersArea (members directory)
  requiresFeature?: string;      // Require feature enabled (e.g., "shop", "bookings")
  external?: boolean;            // External URL flag (default: false, auto-detect if route starts with http)
  target?: '_blank' | '_self';   // Link target (default: '_self' for internal, '_blank' for external)
  icon?: string;                 // Optional icon (emoji/symbol) — pick in Layout → Navigation; used for compact nav tabs when space is tight
  /**
   * When true: signed-out visitors see `guestLabel` (fallback "Login") at `route`;
   * signed-in users see `label` and tap to sign out (homepage). Do not combine with `requiresAuth`.
   */
  isLogin?: boolean;
  /** Label when signed out; only used when `isLogin` is true. */
  guestLabel?: string;
}

/**
 * Get the complete client configuration object.
 * Config is loaded from Firestore at runtime and set in window.__CLIENT_CONFIG by bootstrap.
 * 
 * @returns The client configuration
 * @throws Error if config is not available (should not happen in normal operation)
 */
export function getPageConfig(config: ClientConfig, pageId: string): GenericPageConfig | undefined {
  return config.layout?.pages?.[pageId] as GenericPageConfig | undefined;
}

/** Resolve ordered sections + page chrome from an in-memory client config (bootstrap cache or snapshot). */
export function resolvePageLayoutFromClientConfig(
  config: ClientConfig | null | undefined,
  pageId: string,
): {
  sections: PageSection[];
  pageBackground: PageBackgroundConfig | null;
  contentInsetPx: number;
} {
  if (!config?.layout) {
    return { sections: [], pageBackground: null, contentInsetPx: 0 };
  }

  const pageConfig = config.layout.pages?.[pageId];
  const rawSectionIds = pageConfig?.sectionIds;
  const sectionIdsArray = Array.isArray(rawSectionIds) ? rawSectionIds : undefined;
  const globalSections: PageSection[] = config.layout.sections ?? [];
  let resolved: PageSection[] = [];

  if (sectionIdsArray && sectionIdsArray.length > 0) {
    resolved = sectionIdsArray
      .filter((ref: PageSectionReference) => ref.enabled !== false)
      .map((ref: PageSectionReference) => {
        const section = globalSections.find((s) => s.id === ref.sectionId);
        if (!section) return null;
        const enabled = section.enabled !== false && ref.enabled !== false;
        return { ...section, order: ref.order, enabled };
      })
      .filter((s): s is PageSection => s !== null)
      .sort((a, b) => a.order - b.order);
  }

  const bg = pageConfig?.background as PageBackgroundConfig | undefined;
  const pageBackground = bg && typeof bg === 'object' && bg.type && bg.value ? bg : null;
  const insetRaw = (pageConfig as { contentInsetPx?: number } | undefined)?.contentInsetPx;
  const contentInsetPx = typeof insetRaw === 'number' && insetRaw >= 0 ? insetRaw : 0;

  return { sections: resolved, pageBackground, contentInsetPx };
}

export function isPageUnderConstruction(config: ClientConfig, pageId: string): boolean {
  return getPageConfig(config, pageId)?.underConstruction === true;
}

export function getPageDisplayName(config: ClientConfig, pageId: string): string {
  const page = getPageConfig(config, pageId);
  if (page?.name?.trim()) return page.name.trim();
  return pageId;
}

export function getClientConfig(): ClientConfig {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG as ClientConfig;
  }
  
  // In tests, we might not have window.__CLIENT_CONFIG set yet
  // This should not happen in normal browser operation since bootstrap sets it before rendering
  if (typeof window === 'undefined') {
    throw new Error('getClientConfig() called outside browser context without config');
  }
  
  throw new Error('Client config not loaded. This should not happen - config should be loaded during bootstrap.');
}

/** Layout `layout.pages` id for the public casting apply shell (`/casting/:slug`). */
export const CASTING_APPLY_PAGE_ID = 'castingApply';

/** Layout `layout.pages` id for the public event inquiry shell (`/event-inquiry/:eventId`). */
export const EVENT_INQUIRY_APPLY_PAGE_ID = 'eventInquiryApply';

/** Layout `layout.pages` id for the one-click checkout shell (default `/shop/tickets`). */
export const ONE_CLICK_CHECKOUT_PAGE_ID = 'oneClickCheckout';

/** Default URL path per built-in pageId when not set in config */
const DEFAULT_PAGE_PATHS: Record<string, string> = {
  homepage: '/',
  portfolio: '/portfolio',
  about: '/about',
  bookMe: '/book-me',
  shop: '/shop',
  models: '/models',
  members: '/members',
  privacyPolicy: '/privacy-policy',
  terms: '/terms',
  disclaimer: '/disclaimer',
  securityTest: '/security-test',
  unsubscribe: '/unsubscribe',
  ambassador: '/ambassador',
  service: '/service',
  /** Public apply flow is /casting/:slug; path here is the prefix for admin labels only. */
  [CASTING_APPLY_PAGE_ID]: '/casting',
  /** Public inquiry flow is /event-inquiry/:eventId; path here is the prefix for admin labels only. */
  [EVENT_INQUIRY_APPLY_PAGE_ID]: '/event-inquiry',
  /** Public one-click checkout page; override path/slug in Layout → Pages. */
  [ONE_CLICK_CHECKOUT_PAGE_ID]: '/shop/tickets',
};

/** Union of `layout.pages` keys and built-in ids used by `getPagePath` when a page doc is missing. */
export function allPageIdsForPathResolution(pages: Record<string, unknown> | undefined): string[] {
  return Array.from(new Set([...Object.keys(pages || {}), ...Object.keys(DEFAULT_PAGE_PATHS)]));
}

/**
 * Get the admin base path from config (e.g. '/admin').
 */
export function getAdminPath(config?: ClientConfig): string {
  const c = config ?? (typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null);
  if (!c) return '/admin';
  const path = c.routing?.adminPath?.trim();
  return path && path.startsWith('/') ? path : '/admin';
}

/**
 * Get the URL path for a page from config. Uses layout.pages[pageId].path or slug, or built-in defaults.
 */
export function getPagePath(config: ClientConfig, pageId: string): string {
  const pages = config?.layout?.pages;
  const page = pages?.[pageId] as GenericPageConfig | undefined;
  if (page) {
    const explicit = (page as { path?: string }).path?.trim();
    if (explicit && explicit.startsWith('/')) return explicit;
    const slug = page.slug?.trim();
    if (slug) return slug.startsWith('/') ? slug : `/${slug}`;
  }
  return DEFAULT_PAGE_PATHS[pageId] ?? `/${pageId}`;
}

/**
 * Members area: runway fitting & final booking (requires runway designer permission).
 * Lives under the casting hub: `{members}/casting/runway` and `{members}/casting/runway/{castingProjectId}`.
 */
export function getMemberCastingRunwayPath(config: ClientConfig, castingProjectId?: string): string {
  const base = `${getPagePath(config, 'members')}/casting/runway`;
  const id = castingProjectId?.trim();
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

/**
 * Get the FinishSignIn URL path (same as admin path). Invite links use adminPath?inviteToken= so we don't add a hardcoded route;
 * the admin route shows FinishSignIn when inviteToken is present, otherwise Admin.
 */
export function getFinishSignInPath(config?: ClientConfig): string {
  return getAdminPath(config);
}

/**
 * Get the business name for the current client.
 * @returns Business name string (empty string if config not loaded yet)
 */
export function getBusinessName(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.businessName;
  }
  return '';
}

/**
 * Entity name for the public footer copyright (© year …). Uses {@link getBusinessName} from
 * clientConfigs (CMC “Business name” / same Basics block as site email and address). Empty if unset.
 */
export function getFooterCopyrightEntityName(): string {
  return getBusinessName().trim();
}

/**
 * Get the primary contact email for the current client.
 * @returns Contact email string (empty string if config not loaded yet)
 */
export function getContactEmail(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.contact?.email || '';
  }
  return '';
}

/**
 * Get the logo URL for the current client.
 * If the logo path starts with '/', it's an asset filename stored in Firebase Storage.
 * Constructs the Firebase Storage public URL directly (no admin-server needed).
 * Otherwise, return it as-is (for absolute URLs).
 * @returns Logo URL string
 */
export function getLogoUrl(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return '';
  }
  
  const config = window.__CLIENT_CONFIG as ClientConfig;
  const logoPath = config.branding?.logo;
  
  if (!logoPath) {
    return '';
  }
  
  // If logo path is already a full URL (http/https), return as-is
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  
  // If logo path starts with '/', it's an asset filename stored in Firebase Storage
  // Construct Firebase Storage public URL: https://storage.googleapis.com/{bucket}/clients/{clientId}/assets/{filename}
  if (logoPath.startsWith('/')) {
    const filename = logoPath.slice(1); // Remove leading slash
    
    // Use centralized environment detection
    const environment = getEnvironment();
    
    const storageBucket = getStorageBucket(environment);
    if (!storageBucket) {
      return '';
    }
    // Firebase Storage public URL format: https://storage.googleapis.com/{bucket}/clients/{clientId}/assets/{filename}
    const encodedPath = `clients/${config.clientId}/assets/${encodeURIComponent(filename)}`;
    return `https://storage.googleapis.com/${storageBucket}/${encodedPath}`;
  }
  
  // Return as-is for other paths (shouldn't happen, but just in case)
  return logoPath;
}

/**
 * Resolve a public media URL: absolute http(s) as-is; otherwise treat as a path under
 * `clients/{clientId}/assets/` (leading slash optional).
 *
 * Paths under shipped static folders (`/mock-assets/`, `/mock-templates/`) stay site-local —
 * they live in `public/` and must not be rewritten to Firebase Storage.
 */
export function resolveClientAssetUrl(pathOrUrl: string | undefined | null): string {
  if (!pathOrUrl?.trim()) return '';
  const trimmed = pathOrUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;

  const withoutLeadingSlash = trimmed.replace(/^\/+/, '');
  if (
    withoutLeadingSlash.startsWith('mock-assets/') ||
    withoutLeadingSlash.startsWith('mock-templates/')
  ) {
    const base = (typeof process !== 'undefined' && process.env.PUBLIC_URL
      ? process.env.PUBLIC_URL
      : ''
    ).replace(/\/$/, '');
    return `${base}/${withoutLeadingSlash}`;
  }

  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return trimmed;

  const config = window.__CLIENT_CONFIG as ClientConfig;
  const clientId = config.clientId;
  if (!clientId) return trimmed;

  let relative = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  const assetsRoot = `clients/${clientId}/assets/`;
  const storagePath = relative.startsWith('clients/') ? relative : `${assetsRoot}${relative}`;

  const environment = getEnvironment();
  const storageBucket = getStorageBucket(environment);
  if (!storageBucket) return trimmed;

  return `https://storage.googleapis.com/${storageBucket}/${storagePath.split('/').map(encodeURIComponent).join('/')}`;
}

/**
 * Resolve poweredBy logo URL: absolute http(s) as-is; otherwise treat as a filename in
 * `clients/{clientId}/assets/` (leading slash optional — CMC asset picker uses `/name.png`).
 */
export function getPoweredByLogoUrl(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return '';
  const config = window.__CLIENT_CONFIG as ClientConfig;
  const raw = config.poweredBy?.logoUrl?.trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  const filename = raw.startsWith('/') ? raw.slice(1) : raw;
  if (!filename) return '';
  const environment = getEnvironment();
  const storageBucket = getStorageBucket(environment);
  if (!storageBucket) return '';
  const encodedPath = `clients/${config.clientId}/assets/${encodeURIComponent(filename)}`;
  return `https://storage.googleapis.com/${storageBucket}/${encodedPath}`;
}

/**
 * Get the favicon (browser tab icon) URL for the current client.
 * Uses `branding.favicon` when set (same resolution as site logo paths).
 * When unset, uses the CMC attribution logo if attribution is shown (`poweredBy.show`) and `poweredBy.logoUrl` resolves.
 * @returns Favicon URL string, or empty if neither applies (SeoHead then uses transparent placeholder icons).
 */
export function getFaviconUrl(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return '';
  }
  const config = window.__CLIENT_CONFIG as ClientConfig;
  const path = typeof config.branding?.favicon === 'string' ? config.branding.favicon.trim() : '';
  if (path) {
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (path.startsWith('/')) {
      const filename = path.slice(1);
      const environment = getEnvironment();
      const storageBucket = getStorageBucket(environment);
      if (!storageBucket) return '';
      const encodedPath = `clients/${config.clientId}/assets/${encodeURIComponent(filename)}`;
      return `https://storage.googleapis.com/${storageBucket}/${encodedPath}`;
    }
    return path;
  }
  if (config.poweredBy?.show === true) {
    const fromAttribution = getPoweredByLogoUrl();
    if (fromAttribution) return fromAttribution;
  }
  return '';
}

/**
 * Get the hero image URL for the homepage.
 * Returns empty string if not configured or config not loaded yet.
 */
export function getHeroImageUrl(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.branding?.heroImage || '';
  }
  return '';
}

/**
 * Get the page title for the current client.
 * @returns Page title string (empty string if config not loaded yet)
 */
export function getPageTitle(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.branding?.title || '';
  }
  return '';
}

/**
 * Whether to show the site title text in the navbar.
 * Document title (browser tab) is always set from branding.title via SeoHead; this only controls the nav bar text.
 * @returns true if the title should show in the navbar (default when unset)
 */
export function getShowTitleInNavbar(): boolean {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.branding?.showTitleInNavbar !== false;
  }
  return true;
}

/**
 * Get the meta description for SEO.
 * @returns Meta description string (empty string if config not loaded yet)
 */
export function getPageDescription(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.branding?.metaDescription || '';
  }
  return '';
}

/**
 * Get the OG/social image URL for sharing (Open Graph, Twitter Card).
 * @returns OG image URL or empty string if not set
 */
export function getOgImage(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    return window.__CLIENT_CONFIG.branding?.ogImage || window.__CLIENT_CONFIG.branding?.logo || '';
  }
  return '';
}

/**
 * Get the canonical site URL for OG and canonical tags.
 * @returns siteUrl or current origin
 */
export function getSiteUrl(): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG?.siteUrl) {
    return window.__CLIENT_CONFIG.siteUrl.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

/**
 * Get Instagram configuration for the current client.
 * @returns Instagram configuration object or null if not configured or config not loaded yet
 */
export function getInstagramConfig() {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return null;
  }
  const cfg = window.__CLIENT_CONFIG as ClientConfig;
  if (!cfg.social?.instagram) return null;
  return {
    handles: cfg.social.instagram.handles || [],
    embedScript: cfg.social.instagram.embedScript
  };
}


/**
 * Get Firebase project IDs for the current client.
 * @param environment 'staging' or 'production'
 * @returns Firebase project ID
 */
export function getFirebaseProjectId(environment: 'staging' | 'production' = 'production'): string {
  if (environment === 'staging') {
    return process.env.REACT_APP_FIREBASE_PROJECT_ID || 'unwontedartistry-stage';
  }
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    const cfg = window.__CLIENT_CONFIG as ClientConfig;
    const projectId = cfg.firebase?.projectId;
    if (projectId) return projectId;
  }
  const envProjectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
  if (envProjectId) return envProjectId;
  return process.env.REACT_APP_SHARED_PRODUCTION_PROJECT_ID || '';
}

/**
 * Get Firebase storage bucket for the current client.
 * Primary source is client config; when omitted, derive a safe fallback bucket
 * from project id so asset URLs (logo/favicon/attribution) can still resolve.
 * @param environment 'staging' or 'production'
 * @returns Storage bucket string, or '' if not available
 */
export const STORAGE_BUCKET_REQUIRED_MESSAGE =
  'Storage bucket is required. Add firebase.storageBucket (production) and/or firebase.stagingStorageBucket (staging) to client config in CMC and publish.';

export function getStorageBucket(environment: 'staging' | 'production' = 'production'): string {
  if (typeof window !== 'undefined' && window.__CLIENT_CONFIG) {
    const cfg = window.__CLIENT_CONFIG as ClientConfig;
    const configuredBucket = environment === 'staging'
      ? cfg.firebase?.stagingStorageBucket
      : cfg.firebase?.storageBucket;
    const bucket = String(configuredBucket ?? '').trim();
    if (bucket) return bucket;

    // Fallback only when bucket is missing: derive from project id.
    if (environment === 'staging') {
      const stagingProjectId = String(cfg.firebase?.stagingProjectId ?? '').trim();
      if (stagingProjectId) return `${stagingProjectId}.firebasestorage.app`;
      return '';
    }
    const projectId = String(cfg.firebase?.projectId ?? '').trim();
    return projectId ? `${projectId}.appspot.com` : '';
  }
  return '';
}

/**
 * Check if a feature is enabled for the current client.
 * @param feature Feature name
 * @returns True if enabled, false otherwise (returns false if config not loaded yet)
 */
export function isFeatureEnabled(feature: keyof ClientConfig['features']): boolean {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return false; // Safe default: features disabled until config loads
  }
  const cfg = window.__CLIENT_CONFIG as ClientConfig;
  return cfg.features?.[feature] === true;
}

/**
 * Get contract settings for the current client.
 * @returns Contract configuration object (with defaults if config not loaded yet)
 */
export function getContractSettings() {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return {
      defaultTimeline: '4',
      governingLaw: 'Arizona',
      producerSignature: undefined as ProducerSignatureConfig | undefined,
    };
  }
  const cfg = window.__CLIENT_CONFIG as ClientConfig;
  return {
    defaultTimeline: cfg.contract?.defaultTimeline || '4',
    governingLaw: cfg.contract?.governingLaw || 'Arizona',
    producerSignature: cfg.contract?.producerSignature,
  };
}

/**
 * Default “CC” line for casting applicant email modals: explicit `contract.castingApplicantCommsDefaultCc`, else site
 * `contact.email` when present. Trimmed single-line or list (commas / semicolons / newlines) — same parsing as send.
 */
export function getCastingApplicantCommsDefaultCcLine(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return '';
  const cfg = window.__CLIENT_CONFIG as ClientConfig;
  const explicit = cfg.contract?.castingApplicantCommsDefaultCc?.trim();
  if (explicit) return explicit;
  return (cfg.contact?.email ?? '').trim();
}

/** Default CC line for publication upload invite emails (`publicationHub.submissionInviteDefaultCc`, else contact email). */
export function getPublicationSubmissionInviteDefaultCcLine(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return '';
  const cfg = window.__CLIENT_CONFIG as ClientConfig;
  const explicit = cfg.publicationHub?.submissionInviteDefaultCc?.trim();
  if (explicit) return explicit;
  return (cfg.contact?.email ?? '').trim();
}

/** Resolved line-up music upload cap for this client (default 100 MiB; max 200 MiB). */
export function getCastingLineupMusicMaxBytes(config?: ClientConfig): number {
  const cfg = config ?? getClientConfig();
  return resolveCastingLineupMusicMaxBytes(cfg.castingHub?.lineupMusicMaxBytes);
}

export function getCastingLineupMusicMaxLabel(config?: ClientConfig): string {
  return formatCastingLineupMusicMaxMb(getCastingLineupMusicMaxBytes(config));
}

/** Split CC input into unique trimmed addresses (case preserved), or undefined if empty. */
export function parseCcInputToEmailList(raw: string): string[] | undefined {
  const parts = raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * Get SMS template with variable replacement.
 * @param templateName Template name
 * @param variables Object with variable values
 * @returns Rendered SMS template (empty string if config not loaded yet)
 */
export function getSMSTemplate(
  templateName: keyof ClientConfig['sms']['templates'],
  variables: Record<string, string> = {}
): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return '';
  }
  const cfg = window.__CLIENT_CONFIG as ClientConfig;
  let template = cfg.sms?.templates?.[templateName] || '';
  
  if (!template) {
    return '';
  }
  
  // Replace variables
  Object.entries(variables).forEach(([key, value]) => {
    template = template.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });
  
  // Replace businessName if not already provided
  if (!variables.businessName) {
    template = template.replace(/{{businessName}}/g, cfg.businessName || '');
  }
  
  return template;
}

/**
 * Check if a feature is unlocked (considers subscriptions and one-time purchases)
 * This is a wrapper that uses the payment service for payment-aware checks.
 * For simple feature flag checks, use isFeatureEnabled().
 */
export async function isFeatureUnlocked(feature: string): Promise<boolean> {
  // Import dynamically to avoid circular dependencies
  const { isFeatureUnlocked: checkUnlocked } = await import('./paymentService');
  return checkUnlocked(feature);
}

/**
 * Default navigation links (fallback if config doesn't have navigation)
 */
const DEFAULT_NAV_LINKS: NavLink[] = [
  { id: 'home', label: 'Home', route: '/', order: 0 },
  { id: 'portfolio', label: 'Portfolio', route: '/portfolio', order: 1 },
  { id: 'about', label: 'About', route: '/about', order: 2 },
  { id: 'book', label: 'Book', route: '/book-me', order: 3, requiresFeature: 'bookings' },
  { id: 'members', label: 'Members', route: '/members', order: 98, requiresMembersArea: true },
  { id: 'admin', label: 'Admin', route: '/admin', order: 99, requiresAdmin: true }
];

/**
 * Get navigation links from client config (Layout → Navigation).
 * All links are configured explicitly; order uses each link's `order` field (admin uses ↑/↓ which rewrites order).
 * Legacy rows with {@link EVENT_INQUIRY_NAV_LINK_ID} are dropped (previously merged at read time).
 */
export function getNavigationLinks(): NavLink[] {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return DEFAULT_NAV_LINKS;
  }
  const config = window.__CLIENT_CONFIG as ClientConfig;
  const raw = config.navigation?.links || DEFAULT_NAV_LINKS;
  const base = raw.filter((l) => l.id !== EVENT_INQUIRY_NAV_LINK_ID);
  return [...base].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

/**
 * Whether to show the site navigation bar.
 * Default true when unset.
 * Pass pathname to also honour navigation.hideNavPathPatterns (wildcards supported).
 */
export function getShowNavigation(pathname?: string): boolean {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    return true;
  }
  const config = window.__CLIENT_CONFIG as ClientConfig;
  if (config.navigation?.showNavigation === false) return false;
  if (pathname != null && pathname !== '') {
    if (pathMatchesAnyNavHidePattern(pathname, config.navigation?.hideNavPathPatterns)) {
      return false;
    }
  }
  return true;
}

/**
 * Whether to show the light/dark mode toggle in the nav bar. Default true.
 */
export function getShowThemeToggle(): boolean {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return true;
  const config = window.__CLIENT_CONFIG as ClientConfig;
  return config.navigation?.showThemeToggle !== false;
}

/**
 * When true, the nav bar hides on scroll down and shows on scroll up. Default false (always visible).
 */
export function getHideNavOnScrollDown(): boolean {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return false;
  const config = window.__CLIENT_CONFIG as ClientConfig;
  return config.navigation?.hideNavOnScrollDown === true;
}

/**
 * Navbar background opacity 0–1. Default 1.
 */
export function getNavbarAlpha(): number {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 1;
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.alpha;
  if (v == null || typeof v !== 'number') return 1;
  return Math.max(0, Math.min(1, v));
}

/**
 * When true, the fixed nav bar on top of the page is shown. Default true.
 */
export function getNavBarOnTop(): boolean {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return true;
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.navBarOnTop;
  return v !== false;
}

/**
 * When true, the nav bar in the page flow (sibling to content) is shown. Default false.
 */
export function getNavBarOnPage(): boolean {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return false;
  return (window.__CLIENT_CONFIG as ClientConfig).navigation?.navBarOnPage === true;
}

/**
 * Navbar height in pixels. Default 80.
 */
export function getNavbarHeightPx(): number {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 80;
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.heightPx;
  if (v == null || typeof v !== 'number') return 80;
  return Math.max(48, Math.min(160, v));
}

/** Token names for the theme colour library. Stored values can reference these by name. */
export const THEME_COLOR_TOKENS = ['primary', 'secondary', 'background', 'backgroundAlt', 'text', 'textMuted', 'border', 'accent'] as const;
export type ThemeColorToken = (typeof THEME_COLOR_TOKENS)[number];

/** Default colour library when the client has not saved a palette. Black and white so CMC adds the client's palette and saves it. */
export const DEFAULT_COLOUR_LIBRARY: Record<string, string> = {
  primary: '#000000',
  secondary: '#cccccc',
  background: '#ffffff',
  backgroundAlt: '#f5f5f5',
  text: '#000000',
  textMuted: '#666666',
  border: '#cccccc',
  accent: '#ffffff',
};

/** One of up to 3 custom colour combinations saved from Layout → Colour library. */
export interface CustomColorSlot {
  name: string;
  colors: Record<string, string>;
}

export const CUSTOM_COLOR_SLOT_COUNT = 3;

/** Get the 3 custom colour slots from config (filled with defaults for missing slots). */
export function getCustomColorSlots(config?: ClientConfig): CustomColorSlot[] {
  const c = config ?? (typeof window !== 'undefined' ? (window.__CLIENT_CONFIG as ClientConfig) : undefined);
  const raw = c?.theme?.customColorSlots;
  const slots: CustomColorSlot[] = [];
  for (let i = 0; i < CUSTOM_COLOR_SLOT_COUNT; i++) {
    const entry = Array.isArray(raw) && raw[i] && typeof raw[i] === 'object'
      ? raw[i] as CustomColorSlot
      : null;
    slots.push({
      name: (entry?.name && String(entry.name).trim()) || `Slot ${i + 1}`,
      colors: entry?.colors && typeof entry.colors === 'object' ? { ...entry.colors } : {},
    });
  }
  return slots;
}

/** Returns true if the value is a theme token name (reference-by-name). */
export function isThemeColorToken(value: string | undefined): boolean {
  if (typeof value !== 'string' || !value.trim()) return false;
  return (THEME_COLOR_TOKENS as readonly string[]).includes(value.trim());
}

/**
 * Resolve a colour value: if it's a theme token name, return var(--color-{token}); otherwise return the value as-is (custom CSS).
 */
export function resolveColorValue(value: string | undefined): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const trimmed = value.trim();
  if ((THEME_COLOR_TOKENS as readonly string[]).includes(trimmed)) return `var(--color-${trimmed})`;
  return trimmed;
}

/**
 * For inline styles (e.g. borderColor): theme token → var(--color-*), else pass-through (hex, rgba, etc.).
 */
export function resolveStoredColorToCss(value: string | undefined): string {
  if (typeof value !== 'string' || !value.trim()) return 'var(--color-border, #ccc)';
  const trimmed = value.trim();
  const resolved = resolveColorValue(trimmed);
  return resolved ?? trimmed;
}

/** Convert rgb(r,g,b) or rgba(r,g,b,a) to #rrggbb. Returns input if already hex. */
function cssColorToHex(css: string): string {
  const t = css.trim();
  const hexMatch = t.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (hexMatch) return hexMatch[0].length === 4 ? `#${hexMatch[1][0]}${hexMatch[1][0]}${hexMatch[1][1]}${hexMatch[1][1]}${hexMatch[1][2]}${hexMatch[1][2]}` : t;
  const rgbMatch = t.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgbMatch) {
    const r = Math.max(0, Math.min(255, parseInt(rgbMatch[1], 10)));
    const g = Math.max(0, Math.min(255, parseInt(rgbMatch[2], 10)));
    const b = Math.max(0, Math.min(255, parseInt(rgbMatch[3], 10)));
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  }
  return t;
}

/**
 * Fold 0–1 alpha into a CSS colour for backgrounds. Avoids setting `opacity` on the element (which would hide
 * the fill entirely at 0). Supports hex, rgb/rgba, `var(--*)`, and other colours via color-mix fallback.
 */
export function applyAlphaToCssBackground(cssColor: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  if (a <= 0) return 'transparent';
  if (a >= 1) return cssColor;
  const t = cssColor.trim();
  if (t === 'transparent') return `rgba(0, 0, 0, ${a})`;
  if (t.startsWith('var(')) {
    return `color-mix(in srgb, ${t} ${Math.round(a * 100)}%, transparent)`;
  }
  const rgbaMatch = t.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+)\s*)?\)/);
  if (rgbaMatch) {
    const r = Math.max(0, Math.min(255, parseInt(rgbaMatch[1], 10)));
    const g = Math.max(0, Math.min(255, parseInt(rgbaMatch[2], 10)));
    const b = Math.max(0, Math.min(255, parseInt(rgbaMatch[3], 10)));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  const hexMatch = t.match(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (hexMatch) {
    const full = cssColorToHex(t);
    const h = full.slice(1);
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return `color-mix(in srgb, ${t} ${Math.round(a * 100)}%, transparent)`;
}

/**
 * Resolve a stored colour (token name or custom) to a hex string for use in overlay/card styles that need rgb.
 * For token names, reads computed --color-{token}; for custom, returns as-is (caller may need to parse rgb/rgba).
 */
export function getResolvedColorHex(value: string | undefined): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const trimmed = value.trim();
  if ((THEME_COLOR_TOKENS as readonly string[]).includes(trimmed)) {
    if (typeof document === 'undefined') return undefined;
    const swatches = getComputedThemeColorSwatches();
    const css = swatches[trimmed];
    if (css) return cssColorToHex(css);
    const computed = getComputedStyle(document.documentElement).getPropertyValue(`--color-${trimmed}`).trim();
    if (computed) return cssColorToHex(computed);
    return undefined;
  }
  return cssColorToHex(trimmed);
}

/** Get theme colour library from config (token -> CSS colour). */
export function getThemeColors(): Record<string, string> | undefined {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return undefined;
  const colors = (window.__CLIENT_CONFIG as ClientConfig).theme?.colors;
  return colors && typeof colors === 'object' ? colors : undefined;
}

function pickLibraryColor(library: Record<string, string>, token: ThemeColorToken): string {
  const v = library[token];
  if (typeof v === 'string' && v.trim()) return v.trim();
  return DEFAULT_COLOUR_LIBRARY[token] ?? '#000000';
}

/** Darken a 6-digit hex colour for button hovers (amount 0–1). */
export function darkenHexColor(hex: string, amount = 0.18): string {
  const m = hex.trim().replace(/^#/, '').match(/^([0-9A-Fa-f]{6})$/);
  if (!m) return hex;
  const channel = (start: number) => {
    const n = parseInt(m[1].slice(start, start + 2), 16);
    return Math.max(0, Math.min(255, Math.round(n * (1 - amount))))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(0)}${channel(2)}${channel(4)}`;
}

/** Lighten a 6-digit hex colour (amount 0–1 toward white). */
export function lightenHexColor(hex: string, amount = 0.12): string {
  const m = hex.trim().replace(/^#/, '').match(/^([0-9A-Fa-f]{6})$/);
  if (!m) return hex;
  const channel = (start: number) => {
    const n = parseInt(m[1].slice(start, start + 2), 16);
    return Math.max(0, Math.min(255, Math.round(n + (255 - n) * amount)))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(0)}${channel(2)}${channel(4)}`;
}

export function mergeColourLibrary(
  partial?: Record<string, string> | null
): Record<ThemeColorToken, string> {
  const merged = { ...DEFAULT_COLOUR_LIBRARY, ...(partial ?? {}) };
  const out = {} as Record<ThemeColorToken, string>;
  for (const token of THEME_COLOR_TOKENS) {
    out[token] = pickLibraryColor(merged, token);
  }
  return out;
}

/**
 * Map the 8-token colour library to every shell CSS variable admin + public UI read
 * (`--color-accent-gold` for buttons, `--color-bg`, App.css `--primary`, etc.).
 */
export function buildShellColorVarsFromLibrary(
  partial?: Record<string, string> | null,
  options?: { isDark?: boolean }
): Record<string, string> {
  const lib = mergeColourLibrary(partial);
  const isDark = options?.isDark ?? false;
  const primary = lib.primary;
  const primaryHover = darkenHexColor(primary);
  const bgLight =
    lib.backgroundAlt.trim().toLowerCase() !== lib.background.trim().toLowerCase()
      ? lib.backgroundAlt
      : lightenHexColor(lib.background, 0.06);
  return {
    '--color-primary': primary,
    '--color-secondary': lib.secondary,
    '--color-background': lib.background,
    '--color-backgroundAlt': lib.backgroundAlt,
    '--color-text': lib.text,
    '--color-textMuted': lib.textMuted,
    '--color-border': lib.border,
    '--color-accent': lib.accent,
    '--color-bg': lib.background,
    '--color-bg-alt': lib.backgroundAlt,
    '--color-bg-light': bgLight,
    '--color-text-light': lib.textMuted,
    '--color-accent-gold': primary,
    '--color-accent-gold-dark': primaryHover,
    '--color-accent-gold-light': lightenHexColor(primary, 0.35),
    '--color-accent-dark': lib.text,
    '--color-accent-navy': lib.secondary,
    '--color-pastel-blush': lib.accent,
    '--button-primary-bg': primary,
    '--button-primary-hover': primaryHover,
    '--button-primary-fg': '#ffffff',
    '--button-secondary-bg': lib.backgroundAlt,
    '--button-secondary-hover-bg': bgLight,
    '--button-secondary-text': lib.text,
    '--button-secondary-border': lib.border,
    '--button-secondary-hover-border': primary,
    '--button-ghost-text': lib.text,
    '--button-ghost-hover-bg': bgLight,
    '--button-outline-color': primary,
    '--button-text-color': primary,
    '--button-text-hover-color': primaryHover,
    '--page-surface-color': lib.background,
    '--background': lib.background,
    '--primary': lib.text,
    '--secondary': lib.textMuted,
    '--accent': isDark ? lib.background : lib.accent,
    '--highlight': primary,
    '--card': isDark ? lib.backgroundAlt : lib.background,
    '--border': lib.border,
    '--dark-accent': lib.text,
    '--text': lib.text,
  };
}

/** Apply a colour library (or config) to document CSS variables for live preview + persistence. */
export function applyColourLibraryToDocument(partial?: Record<string, string> | null): void {
  if (typeof document === 'undefined') return;
  const source = partial ?? getThemeColors();
  const isDark = typeof document.body !== 'undefined' && document.body.classList.contains('dark-theme');
  const vars = buildShellColorVarsFromLibrary(source, { isDark });
  const root = document.documentElement;
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
}

/**
 * Apply theme.colors from config to document root CSS variables (--color-{token}).
 * When no config is saved, applies DEFAULT_COLOUR_LIBRARY (black and white) so the site is neutral until the CMC saves the client's palette.
 */
export function applyThemeColorsFromConfig(): void {
  applyColourLibraryToDocument();
}

function resolveTypographyPresetId(raw: string | undefined): string {
  if (raw && FONT_PRESETS[raw]) return raw;
  return DEFAULT_THEME_TYPOGRAPHY_PRESET_ID;
}

/** Resolved preset id per role (invalid or missing → {@link DEFAULT_THEME_TYPOGRAPHY_PRESET_ID}). */
export function getMergedThemeTypographyPresetIds(
  typography: Partial<Record<ThemeTypographyRole, string>> | undefined
): Record<ThemeTypographyRole, string> {
  const out = {} as Record<ThemeTypographyRole, string>;
  for (const role of THEME_TYPOGRAPHY_ROLES) {
    out[role] = resolveTypographyPresetId(typography?.[role]);
  }
  return out;
}

function fontFamilyForTypographyRole(role: ThemeTypographyRole, presetId: string): string {
  const preset = getFontPreset(presetId);
  if (role === 'headline' || role === 'subheadline') {
    return preset.fontFamilyHeading ?? preset.fontFamily;
  }
  return preset.fontFamily;
}

/**
 * Apply resolved typography roles + tuning to CSS variables on the document root.
 */
export function applyThemeTypographyResolved(
  roles: Record<ThemeTypographyRole, string>,
  tuning: ThemeTypographyTuning
): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const role of THEME_TYPOGRAPHY_ROLES) {
    const stack = fontFamilyForTypographyRole(role, roles[role]);
    root.style.setProperty(`--font-text-${role}`, stack);
  }
  const bodyPreset = getFontPreset(roles.body);
  const headlinePreset = getFontPreset(roles.headline);
  root.style.setProperty('--font-body', bodyPreset.fontFamily);
  root.style.setProperty('--font-heading', headlinePreset.fontFamilyHeading ?? headlinePreset.fontFamily);

  const scale =
    tuning.headlineScale === 'large' ? '1.16' : tuning.headlineScale === 'xl' ? '1.32' : '1';
  root.style.setProperty('--typography-headline-scale', scale);
  root.style.setProperty('--typography-label-text-transform', tuning.labelUppercase ? 'uppercase' : 'none');

  for (const role of THEME_TYPOGRAPHY_ROLES) {
    const v = tuning.variants[role];
    root.style.setProperty(`--typography-${role}-size-scale`, String(v.sizeScale));
    root.style.setProperty(`--typography-${role}-line-height`, String(v.lineHeight));
    root.style.setProperty(`--typography-${role}-letter-spacing-em`, String(v.letterSpacingEm));
    root.style.setProperty(`--typography-${role}-margin-top-rem`, String(v.marginTopRem));
    root.style.setProperty(`--typography-${role}-margin-bottom-rem`, String(v.marginBottomRem));
  }

  root.style.setProperty('--typography-hero-headline-sub-scale', String(tuning.hero.headlineSubSizeScale));
  root.style.setProperty('--typography-hero-body-scale', String(tuning.hero.bodyCaptionLabelScale));
}

/**
 * Apply `theme.typography` to `--font-text-{role}` on the document root, and sync legacy `--font-body` / `--font-heading`
 * from the body and headline roles for global `body` / `h1–h6` in App.css.
 */
export function applyThemeTypographyFromConfig(): void {
  if (typeof document === 'undefined') return;
  const cfg =
    typeof window !== 'undefined' && window.__CLIENT_CONFIG ? (window.__CLIENT_CONFIG as ClientConfig) : null;
  const { roles: merged, tuning } = resolveThemeTypographyFromRaw(cfg?.theme?.typography);
  applyThemeTypographyResolved(merged, tuning);
}

/**
 * Live-preview typography in this browser session (updates CSS vars + window config).
 * Call on preset/role edits so heroes/nav update before Save.
 */
export function previewThemeTypographyInSession(input: {
  roles: Record<ThemeTypographyRole, string>;
  tuning: ThemeTypographyTuning;
  customVariants?: CustomTextVariantDefinition[];
}): void {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) {
    applyThemeTypographyResolved(input.roles, input.tuning);
    return;
  }
  const prev = window.__CLIENT_CONFIG as ClientConfig;
  const typography = {
    roles: input.roles,
    tuning: {
      labelUppercase: input.tuning.labelUppercase,
      headlineScale: input.tuning.headlineScale,
      variants: input.tuning.variants,
      hero: input.tuning.hero,
    },
    ...(input.customVariants ? { customVariants: input.customVariants } : {}),
  };
  window.__CLIENT_CONFIG = {
    ...prev,
    theme: { ...prev.theme, typography },
  };
  applyThemeTypographyResolved(input.roles, input.tuning);
  window.dispatchEvent(new CustomEvent('theme-typography-changed'));
}

/** Get current palette for swatches: from config theme.colors, else DEFAULT_COLOUR_LIBRARY (black and white). */
export function getComputedThemeColorSwatches(): Record<string, string> {
  const out: Record<string, string> = {};
  const fromConfig = getThemeColors();
  const source = fromConfig && typeof fromConfig === 'object' && Object.keys(fromConfig).length > 0
    ? fromConfig
    : DEFAULT_COLOUR_LIBRARY;
  for (const token of THEME_COLOR_TOKENS) {
    const cssVal = source[token] ?? DEFAULT_COLOUR_LIBRARY[token];
    if (cssVal) out[token] = cssVal;
  }
  return out;
}

/**
 * Navbar background colour. May be a theme token name (e.g. "primary") or custom CSS; returns resolved CSS value for use in styles.
 */
export function getNavbarBackgroundColor(): string | undefined {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return undefined;
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.backgroundColor;
  return resolveColorValue(typeof v === 'string' ? v : undefined);
}

/**
 * Navbar text/link colour. May be a theme token name or custom CSS; returns resolved CSS value for use in styles.
 */
export function getNavbarTextColor(): string | undefined {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return undefined;
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.textColor;
  return resolveColorValue(typeof v === 'string' ? v : undefined);
}

/**
 * Shell / `.app-content` background when `navigation.appContentBackgroundColor` is set (theme token or custom CSS).
 * When undefined, App.css uses `var(--color-bg)`.
 */
export function getAppContentBackgroundCss(): string | undefined {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return undefined;
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.appContentBackgroundColor;
  return resolveColorValue(typeof v === 'string' ? v : undefined);
}

/** Platform legal pages (privacy, terms, disclaimer). Default `light` for legibility on dark shells. */
export function getLegalPagesSurface(): 'light' | 'theme' {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 'light';
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.legalPagesSurface;
  return v === 'theme' ? 'theme' : 'light';
}

/**
 * Style of the border/divider between navbar and hero. Default 'solid'.
 */
export function getHeroBorderStyle(): 'solid' | 'zigzag' | 'drippy' | 'waves' | 'slanted' | 'none' {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 'solid';
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.heroBorderStyle;
  if (v === 'zigzag' || v === 'drippy' || v === 'waves' || v === 'slanted' || v === 'none') return v;
  return 'solid';
}

export type NavLayoutVariant = 'classic' | 'editorial';

export type EditorialNavLinksPresentation = 'inline' | 'dropdown';

/** Navbar layout preset. Omit or unknown = classic. */
export function getNavLayoutVariant(): NavLayoutVariant {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 'classic';
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.navLayoutVariant;
  return v === 'editorial' ? 'editorial' : 'classic';
}

/** Editorial layout only. Omit or unknown = inline links. */
export function getEditorialNavLinksPresentation(): EditorialNavLinksPresentation {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 'inline';
  const v = (window.__CLIENT_CONFIG as ClientConfig).navigation?.editorialNavLinksPresentation;
  return v === 'dropdown' ? 'dropdown' : 'inline';
}

/** Label on the editorial nav dropdown trigger. Defaults to `Pages`. */
export function getEditorialNavDropdownTriggerLabel(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return 'Pages';
  const raw = (window.__CLIENT_CONFIG as ClientConfig).navigation?.editorialNavDropdownTriggerLabel;
  const t = typeof raw === 'string' ? raw.trim() : '';
  return t.length > 0 ? t : 'Pages';
}

/** Optional tagline under site title (editorial layout). */
export function getNavigationBrandTagline(): string {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return '';
  const t = (window.__CLIENT_CONFIG as ClientConfig).navigation?.brandTagline;
  return typeof t === 'string' ? t.trim() : '';
}

/** Primary navbar CTA when configured and visible; null if absent or invalid. */
export function getNavigationPrimaryCta(): { label: string; route: string; external: boolean } | null {
  if (typeof window === 'undefined' || !window.__CLIENT_CONFIG) return null;
  const c = (window.__CLIENT_CONFIG as ClientConfig).navigation?.primaryCta;
  if (!c || typeof c !== 'object') return null;
  const label = typeof c.label === 'string' ? c.label.trim() : '';
  const route = typeof c.route === 'string' ? c.route.trim() : '';
  if (!label || !route) return null;
  if (c.visible === false) return null;
  const external =
    c.external === true || route.startsWith('http://') || route.startsWith('https://');
  return { label, route, external };
}

/**
 * Check if a nav link should be visible based on user state and feature requirements
 */
export function isNavLinkVisible(
  link: NavLink,
  user: any | null,
  isAdmin: boolean,
  config: ClientConfig
): boolean {
  // Check visible flag
  if (link.visible === false) {
    return false;
  }

  // Check admin requirement
  if (link.requiresAdmin && !isAdmin) {
    return false;
  }

  // Check auth requirement (login/logout pair is visible to guests and signed-in users)
  if (link.requiresAuth && !user && !link.isLogin) {
    return false;
  }

  // Check feature requirement
  if (link.requiresFeature) {
    const featureEnabled = config.features?.[link.requiresFeature as keyof ClientConfig['features']] === true;
    if (!featureEnabled) {
      return false;
    }
  }

  return true;
}


